import { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { Shield, Clock, Sparkles, AlertTriangle, Zap, ChevronDown, ChevronUp, X, MapPin } from "lucide-react";
import { theme, card, btnPrimary, inputStyle, floatingSheet, emptyFrame, countTag, type as text } from "../theme";
import { IndexTabs, RulerProgress, StatusTag } from "../components/Drawing";
import { api, apiErrorMessage } from "../api/client";
import { ControlPicker } from "../components/ControlPicker";
import { fmtDate } from "../lib/format";
import { toDateInput, dateInputToIso, fmtDateOnly, localDateInput, todayInput } from "../lib/dateOnly";
import { useCurrentUser } from "../auth/useCurrentUser";
import { DetailFields, DetailSection, EditButton, RecordActions } from "../components/RecordView";
import { RelatedRecords, type RelatedLink } from "../components/RelatedRecords";
import { PortalLink } from "../components/PortalLink";
import { useFrameworks, FRAMEWORK_LABELS } from "../context/FrameworksContext";
import type { FrameworkId } from "../context/FrameworksContext";
import { scoreColor, scoreBg, scoreLabel, severityOf, countBySeverity, SEVERITY_ORDER, SEVERITY_META } from "../lib/riskScoring";
import { EntityTagsRow, EntityTagFilters, useEntityTagData, useTagFilter, matchesEntityTagFilter, entityRef, KIND_HREFS } from "../components/EntityTags";
import { PlaybookBar } from "../components/PlaybookBar";
import { NextSteps, type NextStepItem } from "../components/NextSteps";
import {
  AppetiteChip, AppetiteVerdict, AcceptanceHint, RiskAppetiteDrawer, loadRiskAppetite, latestAcceptanceExpiry,
  type AppetiteAssessment, type RiskAppetiteView,
} from "../components/RiskAppetite";

import { cbPalette, cbSafe } from "../lib/colourBlind";
// ── Types ─────────────────────────────────────────────────────────────────

interface Treatment {
  id: string;
  type: "mitigate" | "accept" | "transfer" | "avoid";
  description: string;
  owner?: string | null;
  dueDate?: string | null;
  status: "planned" | "in_progress" | "completed";
  /** "accept" treatments: when the owner accepted the residual risk (date-only). */
  acceptedAt?: string | null;
  /** "accept" treatments above appetite: who approved the acceptance (not the owner). */
  acceptanceApprovedBy?: string | null;
  /** "accept" treatments: when the acceptance lapses and needs re-approval (date-only). */
  acceptanceExpiresAt?: string | null;
}

interface AiEvaluation {
  id: string;
  inherentLikelihood: number;
  inherentImpact: number;
  inherentScore: number;
  inherentLikelihoodRationale: string;
  inherentImpactRationale: string;
  residualLikelihood: number;
  residualImpact: number;
  residualScore: number;
  residualLikelihoodRationale: string;
  residualImpactRationale: string;
  modelUsed: string;
  createdAt: string;
}

interface Risk {
  id: string;
  displayId?: number | null;
  title: string;
  category: string;
  threat: string;
  vulnerability?: string | null;
  assetOrProcess?: string | null;
  /** The asset-register row this risk concerns (Risk.assetId), when one is named. */
  asset?: { id: string; name: string } | null;
  assetId?: string | null;
  /** A risk transferred to an insurer names the policy it relies on. */
  insurancePolicyId?: string | null;
  insurancePolicy?: { id: string; insurer: string; policyNumber?: string | null } | null;
  owner?: string | null;
  /** The member the owner is (lib/ownership.ts): chosen, or matched exactly from the text. */
  ownerUserId?: string | null;
  assuranceOwner?: string | null;
  targetScore?: number | null;
  priority?: string | null;
  reviewDate?: string | null;
  status: string;
  suggestedForRemoval: boolean;
  suggestedForRemovalFrameworks: string[];
  frameworkIds: string[];
  linkedControlId?: string | null;
  linkedControl?: { id: string; name: string; frameworkId: string } | null;
  siteId?: string | null;
  site?: { id: string; name: string; country: string } | null;
  inherentLikelihood?: number | null;
  inherentImpact?: number | null;
  inherentScore?: number | null;
  inherentOverridden: boolean;
  residualLikelihood?: number | null;
  residualImpact?: number | null;
  residualScore?: number | null;
  residualOverridden: boolean;
  treatments: Treatment[];
  evaluations: AiEvaluation[];
  createdAt: string;
  updatedAt: string;
}

// ── Constants ─────────────────────────────────────────────────────────────

const CATEGORIES = ["Confidentiality", "Availability", "Integrity", "Operational", "Financial", "Legal", "Reputational"] as const;

// ── Linked-control drill-down helpers ─────────────────────────────────────

function controlRef(frameworkId: string, controlId: string): string {
  if (frameworkId === "pci_dss") return controlId.replace(/^pci-req-/, "");
  if (frameworkId.startsWith("iasme")) {
    const m = controlId.match(/-(t\d+)$/);
    return m ? m[1] : controlId;
  }
  const prefix = frameworkId.replace(/_/g, "-") + "-";
  return controlId.startsWith(prefix) ? controlId.slice(prefix.length) : controlId;
}

function controlGroup(frameworkId: string, controlId: string): string {
  const ref = controlRef(frameworkId, controlId);
  if (frameworkId === "pci_dss") return `Req ${ref.split(".")[0]}`;
  if (frameworkId.startsWith("iasme")) {
    const m = ref.match(/^t(\d+)/);
    return m ? `Theme ${m[1]}` : ref;
  }
  const parts = ref.split(".");
  if (parts[0] === "A" || parts[0] === "B") return `${parts[0]}.${parts[1]}`;
  return parts[0];
}

function groupLabel(frameworkId: string, group: string): string {
  if (frameworkId === "pci_dss") return `Requirement ${group.replace("Req ", "")}`;
  if (frameworkId.startsWith("iasme")) return group;
  if (group.startsWith("A.") || group.startsWith("B.")) return `Annex ${group}`;
  return `Clause ${group}`;
}
const STATUSES = ["open", "under_treatment", "accepted", "transferred", "tentative_remediated", "closed"] as const;
const TREATMENT_TYPES = ["mitigate", "accept", "transfer", "avoid"] as const;
const TREATMENT_STATUSES = ["planned", "in_progress", "completed"] as const;
const PRIORITIES = ["low", "medium", "high", "critical"] as const;

// ── Score helpers ─────────────────────────────────────────────────────────

function matrixCellColor(l: number, i: number): string {
  return scoreBg(l * i);
}

function statusLabel(s: string): string {
  return { open: "Open", under_treatment: "In treatment", accepted: "Accepted", transferred: "Transferred", tentative_remediated: "Tentatively remediated", closed: "Closed" }[s] ?? s;
}

// Theme tokens, never literals, so the pill follows the dark, light and Jersey schemes.
const STATUS_TONE: Record<string, { color: string; bg: string }> = {
  open:                 { color: theme.color.accentText,  bg: theme.color.accentBg },
  under_treatment:      { color: theme.color.warningText, bg: theme.color.warningBg },
  accepted:             { color: theme.color.successText, bg: theme.color.successBg },
  transferred:          { color: theme.color.consultant,  bg: theme.color.consultantBg },
  tentative_remediated: { color: theme.color.auditor,     bg: theme.color.auditorBg },
  closed:               { color: theme.color.muted,       bg: theme.color.panelAlt },
};

function StatusPill({ status }: { status: string }) {
  const t = STATUS_TONE[status] ?? STATUS_TONE.closed;
  return <span style={{ fontSize: 11, padding: "3px 8px", borderRadius: 3, background: t.bg, color: t.color, fontWeight: 600, display: "inline-block", lineHeight: 1.3 }}>{statusLabel(status)}</span>;
}

function priorityColor(p: string | null | undefined): string {
  return { low: theme.color.riskLowText, medium: theme.color.riskMedText, high: theme.color.riskHighText, critical: theme.color.riskCritText }[p ?? ""] ?? theme.color.muted;
}

function priorityBg(p: string | null | undefined): string {
  return { low: theme.color.riskLowBg, medium: theme.color.riskMedBg, high: theme.color.riskHighBg, critical: theme.color.riskCritBg }[p ?? ""] ?? "transparent";
}

function riskId(r: Risk): string {
  if (r.displayId) return `RISK-${String(r.displayId).padStart(3, "0")}`;
  return "";
}

// Guide-recommended review cadence by severity: critical monthly, high/medium
// at least quarterly, low annually.
function reviewIntervalMonths(score: number | null): number {
  if (!score) return 12;
  const sev = severityOf(score);
  return sev === "critical" ? 1 : sev === "high" || sev === "medium" ? 3 : 12;
}

function isOverdueReview(r: Risk): boolean {
  if (!r.reviewDate) return false;
  return new Date(r.reviewDate) < new Date();
}

function hasOverdueTreatment(r: Risk): boolean {
  return r.treatments.some(t => t.dueDate && t.status !== "completed" && new Date(t.dueDate) < new Date());
}

/** "Accepted: 3 Oct 2026 · Approved by: … · Expires: …" for an accept treatment. */
function acceptanceSummary(t: Treatment): string {
  return [
    `Accepted: ${t.acceptedAt ? fmtDateOnly(t.acceptedAt) : "not recorded"}`,
    `Approved by: ${t.acceptanceApprovedBy || "not recorded"}`,
    `Expires: ${t.acceptanceExpiresAt ? fmtDateOnly(t.acceptanceExpiresAt) : "not recorded"}`,
  ].join(" · ");
}

function treatmentSummary(r: Risk): string | null {
  if (r.treatments.length === 0) return null;
  const done = r.treatments.filter(t => t.status === "completed").length;
  return `${done}/${r.treatments.length}`;
}

// ── Score badge ───────────────────────────────────────────────────────────

function ScoreBadge({ score, size = "md" }: { score: number | null | undefined; size?: "sm" | "md" }) {
  const fontSize = size === "sm" ? 11 : 13;
  const padding  = size === "sm" ? "2px 8px" : "3px 10px";
  return (
    <span style={{
      display: "inline-block",
      fontSize, fontWeight: 700, padding,
      borderRadius: 3,
      background: scoreBg(score),
      color: scoreColor(score),
      minWidth: 28, textAlign: "center",
    }}>
      {score ?? "—"}
    </span>
  );
}

// ── 5×5 Risk Matrix ───────────────────────────────────────────────────────

// The organisation's overall appetite threshold (operating parameter risk.appetite_score, served by
// GET /risks/appetite as params.appetiteScore); 12 only until that answer has loaded (DOC-3).
const DEFAULT_APPETITE_SCORE = 12;

function RiskMatrix({ risks, appetiteScore = DEFAULT_APPETITE_SCORE }: { risks: Risk[]; appetiteScore?: number }) {
  const CELL = 64;
  const LABEL = 32;
  const impacts    = [5, 4, 3, 2, 1];
  const likelihoods = [1, 2, 3, 4, 5];

  const impactLabels:    Record<number, string> = { 5: "Catastrophic", 4: "Major", 3: "Significant", 2: "Minor", 1: "Negligible" };
  const likelihoodLabels: Record<number, string> = { 1: "Rare", 2: "Unlikely", 3: "Possible", 4: "Likely", 5: "Almost certain" };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 0 }}>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", width: 18, marginRight: 4, marginTop: LABEL }}>
          <span style={{ fontSize: 10, color: theme.color.subtle, writingMode: "vertical-rl", transform: "rotate(180deg)", whiteSpace: "nowrap" }}>
            Impact ↑
          </span>
        </div>

        <div>
          {impacts.map((imp) => (
            <div key={imp} style={{ display: "flex", alignItems: "center" }}>
              <div style={{ width: LABEL + 48, fontSize: 10, color: theme.color.subtle, textAlign: "right", paddingRight: 6, flexShrink: 0 }}>
                {imp} — {impactLabels[imp]}
              </div>

              {likelihoods.map((lik) => {
                const inherentHere = risks.filter(r => r.inherentLikelihood === lik && r.inherentImpact === imp);
                const residualHere = risks.filter(r => r.residualLikelihood === lik && r.residualImpact === imp);
                const score = lik * imp;
                const isAppetiteBoundary = score > appetiteScore;
                return (
                  <div key={lik} title={`L${lik} × I${imp} = ${score} (${scoreLabel(score)})`} style={{
                    width: CELL, height: CELL,
                    background: matrixCellColor(lik, imp),
                    border: isAppetiteBoundary
                      ? cbSafe("2px solid rgba(239,68,68,0.35)")
                      : "1px solid rgba(0,0,0,0.08)",
                    display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-start",
                    padding: 4, position: "relative", boxSizing: "border-box",
                  }}>
                    <span style={{ fontSize: 14, fontWeight: 800, color: scoreColor(score), lineHeight: 1, marginTop: 2 }}>{score}</span>
                    <span style={{ fontSize: 7.5, fontWeight: 700, color: scoreColor(score), opacity: 0.85, textTransform: "uppercase", letterSpacing: "0.03em", marginTop: 1 }}>
                      {scoreLabel(score)}
                    </span>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 3, justifyContent: "center", marginTop: 3 }}>
                      {inherentHere.map(r => (
                        <div key={`i-${r.id}`} title={`${riskId(r)} ${r.title} (inherent)`} style={{
                          width: 10, height: 10, borderRadius: "50%",
                          border: `2px solid ${scoreColor(r.inherentScore)}`,
                          background: "transparent", flexShrink: 0,
                        }} />
                      ))}
                      {residualHere.map(r => (
                        <div key={`r-${r.id}`} title={`${riskId(r)} ${r.title} (residual)`} style={{
                          width: 10, height: 10, borderRadius: "50%",
                          background: scoreColor(r.residualScore),
                          flexShrink: 0,
                        }} />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}

          <div style={{ display: "flex", marginLeft: LABEL + 48 }}>
            {likelihoods.map((lik) => (
              <div key={lik} style={{ width: CELL, fontSize: 9, color: theme.color.subtle, textAlign: "center", paddingTop: 4 }}>
                {lik} — {likelihoodLabels[lik]}
              </div>
            ))}
          </div>
          <div style={{ textAlign: "center", marginLeft: LABEL + 48, fontSize: 10, color: theme.color.subtle, paddingTop: 2 }}>
            Likelihood →
          </div>
          <div style={{ textAlign: "center", marginLeft: LABEL + 48, fontSize: 10, color: theme.color.faint, paddingTop: 6 }}>
            Score = Likelihood × Impact · Inherent is scored before controls, residual after
          </div>
        </div>
      </div>

      <div style={{ display: "flex", gap: 16, marginTop: 12, fontSize: 11, color: theme.color.subtle, flexWrap: "wrap" }}>
        <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
          <span style={{ width: 10, height: 10, borderRadius: "50%", border: "2px solid #6366f1", display: "inline-block" }} />
          Inherent (before treatment)
        </span>
        <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
          <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#6366f1", display: "inline-block" }} />
          Residual (after treatment)
        </span>
        <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
          <span style={{ width: 12, height: 12, borderRadius: 2, border: cbSafe("2px solid rgba(239,68,68,0.35)"), display: "inline-block" }} />
          Above risk appetite (score &gt; {appetiteScore})
        </span>
        {[
          { label: "Low (1–4)", bg: theme.color.riskLowBg },
          { label: "Medium (5–9)", bg: theme.color.riskMedBg },
          { label: "High (10–12)", bg: theme.color.riskHighBg },
          { label: "Critical (15+)", bg: theme.color.riskCritBg },
        ].map(z => (
          <span key={z.label} style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <span style={{ width: 12, height: 12, borderRadius: 2, background: z.bg, border: "1px solid rgba(0,0,0,0.1)", display: "inline-block" }} />
            {z.label}
          </span>
        ))}
      </div>
    </div>
  );
}

// ── Portfolio score trend ─────────────────────────────────────────────────

function TrendChart({ risks, appetiteScore = DEFAULT_APPETITE_SCORE }: { risks: Risk[]; appetiteScore?: number }) {
  const points: Array<{ date: string; score: number }> = [];
  for (const risk of risks) {
    for (const ev of risk.evaluations) {
      if (ev.residualScore != null) {
        points.push({ date: ev.createdAt.slice(0, 10), score: ev.residualScore });
      }
    }
  }

  if (points.length === 0) {
    return (
      <div style={{ ...emptyFrame, padding: "32px 16px", color: theme.color.subtle, fontSize: 13 }}>
        No AI evaluation history yet. Run &ldquo;Suggest with AI&rdquo; to generate scored evaluations that will appear here.
      </div>
    );
  }

  const byDate: Record<string, number[]> = {};
  for (const p of points) {
    (byDate[p.date] ??= []).push(p.score);
  }
  const series = Object.entries(byDate)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, scores]) => ({ date, avg: scores.reduce((s, v) => s + v, 0) / scores.length }));

  const today = new Date().toISOString().slice(0, 10);
  const currentScores = risks.map(r => r.residualScore ?? r.inherentScore ?? 0).filter(s => s > 0);
  if (currentScores.length > 0 && !byDate[today]) {
    series.push({ date: today, avg: currentScores.reduce((s, v) => s + v, 0) / currentScores.length });
  }

  const W = 640, H = 200, PX = 40, PY = 20;
  const maxScore = Math.max(...series.map(s => s.avg), 25);
  const scaleY = (v: number) => PY + (H - PY * 2) * (1 - v / maxScore);
  const scaleX = (i: number) => PX + (i / Math.max(series.length - 1, 1)) * (W - PX * 2);

  const pathD = series.map((pt, i) => `${i === 0 ? "M" : "L"} ${scaleX(i)} ${scaleY(pt.avg)}`).join(" ");
  const areaD = `${pathD} L ${scaleX(series.length - 1)} ${H - PY} L ${scaleX(0)} ${H - PY} Z`;

  const scoreColour = (v: number) => v <= 4 ? cbSafe("#22c55e") : v <= 9 ? "#f59e0b" : v <= 12 ? "#f97316" : cbSafe("#ef4444");
  const lastScore = series[series.length - 1].avg;

  // Category breakdown
  const byCategory: Record<string, number> = {};
  for (const r of risks) {
    const s = r.residualScore ?? r.inherentScore ?? 0;
    byCategory[r.category] = (byCategory[r.category] ?? 0) + s;
  }
  const topCategories = Object.entries(byCategory).sort(([, a], [, b]) => b - a).slice(0, 5);

  return (
    <div>
      <div style={{ display: "flex", gap: 24, marginBottom: 16, flexWrap: "wrap" }}>
        <div style={{ ...card, padding: "10px 18px", minWidth: 130 }}>
          <div style={{ fontSize: 10, color: theme.color.subtle, marginBottom: 4 }}>Current avg residual</div>
          <div style={{ fontSize: 24, fontWeight: 700, color: scoreColour(lastScore) }}>{lastScore.toFixed(1)}</div>
          <div style={{ fontSize: 10, color: theme.color.subtle }}>out of 25</div>
        </div>
        <div style={{ ...card, padding: "10px 18px", minWidth: 130 }}>
          <div style={{ fontSize: 10, color: theme.color.subtle, marginBottom: 4 }}>Trend over {series.length} snapshots</div>
          {series.length >= 2 ? (
            <div style={{ fontSize: 14, fontWeight: 700, color: lastScore < series[0].avg ? theme.color.success : theme.color.danger }}>
              {lastScore < series[0].avg ? "▼ Improving" : "▲ Worsening"}
              <span style={{ fontSize: 11, fontWeight: 400, marginLeft: 6, color: theme.color.subtle }}>
                {Math.abs(lastScore - series[0].avg).toFixed(1)} pts
              </span>
            </div>
          ) : (
            <div style={{ fontSize: 12, color: theme.color.subtle }}>Not enough data</div>
          )}
        </div>
        <div style={{ ...card, padding: "10px 18px", minWidth: 130 }}>
          <div style={{ fontSize: 10, color: theme.color.subtle, marginBottom: 4 }}>Risks with evaluations</div>
          <div style={{ fontSize: 24, fontWeight: 700, color: theme.color.heading }}>
            {risks.filter(r => r.evaluations.length > 0).length}
          </div>
          <div style={{ fontSize: 10, color: theme.color.subtle }}>of {risks.length}</div>
        </div>
      </div>

      <div style={{ overflowX: "auto" }}>
        <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", maxWidth: W, display: "block" }}>
          {[0, 25 / 4, 25 / 2, 25 * 3 / 4, 25].map((v, i) => (
            <g key={i}>
              <line x1={PX} y1={scaleY(v)} x2={W - PX} y2={scaleY(v)} stroke="var(--c-border)" strokeWidth="0.5" />
              <text x={PX - 4} y={scaleY(v) + 3.5} fontSize="8" fill="var(--c-faint)" textAnchor="end">{Math.round(v)}</text>
            </g>
          ))}
          <rect x={PX} y={scaleY(25)} width={W - PX * 2} height={scaleY(12) - scaleY(25)} fill={cbSafe("#ef444410")} />
          <rect x={PX} y={scaleY(12)} width={W - PX * 2} height={scaleY(9) - scaleY(12)} fill="#f9731610" />
          <rect x={PX} y={scaleY(9)} width={W - PX * 2} height={scaleY(4) - scaleY(9)} fill="#f59e0b10" />
          <rect x={PX} y={scaleY(4)} width={W - PX * 2} height={scaleY(0) - scaleY(4)} fill={cbSafe("#22c55e10")} />
          {/* The organisation's overall risk appetite threshold */}
          <line x1={PX} y1={scaleY(appetiteScore)} x2={W - PX} y2={scaleY(appetiteScore)} stroke="var(--c-dangerText)" strokeWidth="1" strokeDasharray="4 3" opacity="0.5" />
          <text x={W - PX + 2} y={scaleY(appetiteScore) + 3} fontSize="7" fill="var(--c-dangerText)" opacity="0.7">appetite {appetiteScore}</text>
          <path d={areaD} fill="#6366f118" />
          <path d={pathD} fill="none" stroke="#6366f1" strokeWidth="2" strokeLinejoin="round" />
          {series.map((pt, i) => (
            <g key={i}>
              <circle cx={scaleX(i)} cy={scaleY(pt.avg)} r={4} fill={scoreColour(pt.avg)} stroke="var(--c-panel)" strokeWidth="1.5" />
              {(i === 0 || i === series.length - 1 || series.length <= 8) && (
                <text x={scaleX(i)} y={H - PY + 12} fontSize="8" fill="var(--c-faint)" textAnchor="middle">
                  {pt.date.slice(5)}
                </text>
              )}
            </g>
          ))}
          <text x={10} y={H / 2} fontSize="8" fill="var(--c-subtle)" textAnchor="middle" transform={`rotate(-90 10 ${H / 2})`}>
            Avg residual score
          </text>
        </svg>
      </div>

      {/* Category breakdown */}
      {topCategories.length > 0 && (
        <div style={{ marginTop: 20 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: theme.color.heading, marginBottom: 10 }}>Risk exposure by category</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {topCategories.map(([cat, total]) => {
              const maxTotal = topCategories[0][1];
              return (
                <div key={cat} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ width: 110, fontSize: 11, color: theme.color.subtle, flexShrink: 0 }}>{cat}</div>
                  <div style={{ flex: 1, height: 8, background: "var(--c-border)", borderRadius: 4, overflow: "hidden" }}>
                    <div style={{ height: "100%", width: `${(total / maxTotal) * 100}%`, background: "#6366f1", borderRadius: 4, transition: "width 0.4s" }} />
                  </div>
                  <div style={{ fontSize: 11, color: theme.color.subtle, width: 28, textAlign: "right" }}>{total}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div style={{ marginTop: 10, display: "flex", gap: 16, fontSize: 11, color: theme.color.subtle, flexWrap: "wrap" }}>
        {[{ label: "Low (1–4)", bg: theme.color.riskLowBg }, { label: "Medium (5–9)", bg: theme.color.riskMedBg }, { label: "High (10–12)", bg: theme.color.riskHighBg }, { label: "Critical (15+)", bg: theme.color.riskCritBg }].map(b => (
          <span key={b.label} style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <span style={{ width: 12, height: 12, borderRadius: 2, background: b.bg, border: "1px solid rgba(0,0,0,0.08)", display: "inline-block" }} />
            {b.label}
          </span>
        ))}
      </div>
    </div>
  );
}

// ── Treatment form ────────────────────────────────────────────────────────

function TreatmentForm({
  onSave, onCancel, initial, appetite, riskOwner,
}: {
  onSave: (t: Omit<Treatment, "id">) => Promise<void> | void;
  onCancel: () => void;
  initial?: Partial<Treatment>;
  /** The risk's verdict against appetite, for what an acceptance needs (GET /risks/appetite). */
  appetite?: AppetiteAssessment | null;
  riskOwner?: string | null;
}) {
  const [type, setType]     = useState<Treatment["type"]>(initial?.type ?? "mitigate");
  const [desc, setDesc]     = useState(initial?.description ?? "");
  const [owner, setOwner]   = useState(initial?.owner ?? "");
  const [due, setDue]       = useState(toDateInput(initial?.dueDate));
  const [status, setStatus] = useState<Treatment["status"]>(initial?.status ?? "planned");
  const [accepted, setAccepted] = useState(toDateInput(initial?.acceptedAt));
  const [approver, setApprover] = useState(initial?.acceptanceApprovedBy ?? "");
  const [expires, setExpires]   = useState(toDateInput(initial?.acceptanceExpiresAt));
  // A ref as well as state: a double click fires twice before the disabled button renders (DOC-2).
  const savingRef = useRef(false);
  const [saving, setSaving] = useState(false);
  async function save() {
    if (!desc.trim() || savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    try {
      await onSave({ type, description: desc.trim(), owner: owner || undefined, dueDate: dateInputToIso(due) ?? undefined, status, ...(type === "accept" ? { acceptedAt: dateInputToIso(accepted) ?? null, acceptanceApprovedBy: approver.trim() || null, acceptanceExpiresAt: dateInputToIso(expires) ?? null } : {}) });
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  }
  // Hints only: the server decides whether an acceptance meets the rules (lib/riskAppetite.ts).
  const aboveAppetite = appetite?.verdict === "above";
  const acceptOwner = (owner || riskOwner || "").trim().toLowerCase();
  const approverIsOwner = !!approver.trim() && !!acceptOwner && approver.trim().toLowerCase() === acceptOwner;
  const latestExpiry = aboveAppetite && appetite?.maxAcceptanceMonths != null && accepted ? latestAcceptanceExpiry(accepted, appetite.maxAcceptanceMonths) : null;

  return (
    <div style={{ background: theme.color.panelAlt, borderRadius: 3, padding: 14, marginTop: 8 }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
        <div>
          <label style={{ fontSize: 11, color: theme.color.subtle, display: "block", marginBottom: 4 }}>Type</label>
          <select value={type} onChange={e => setType(e.target.value as Treatment["type"])} style={{ ...inputStyle, width: "100%" }}>
            {TREATMENT_TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
          </select>
        </div>
        <div>
          <label style={{ fontSize: 11, color: theme.color.subtle, display: "block", marginBottom: 4 }}>Status</label>
          <select value={status} onChange={e => setStatus(e.target.value as Treatment["status"])} style={{ ...inputStyle, width: "100%" }}>
            {TREATMENT_STATUSES.map(s => <option key={s} value={s}>{s.replace("_", " ").replace(/^\w/, c => c.toUpperCase())}</option>)}
          </select>
        </div>
        <div>
          <label style={{ fontSize: 11, color: theme.color.subtle, display: "block", marginBottom: 4 }}>Owner (optional)</label>
          <input value={owner} onChange={e => setOwner(e.target.value)} placeholder="e.g. IT Manager" style={{ ...inputStyle, width: "100%", boxSizing: "border-box" }} />
        </div>
        <div>
          <label style={{ fontSize: 11, color: theme.color.subtle, display: "block", marginBottom: 4 }}>Due date (optional)</label>
          <input type="date" value={due} onChange={e => setDue(e.target.value)} style={{ ...inputStyle, width: "100%", boxSizing: "border-box" }} />
        </div>
        {type === "accept" && (
          <div style={{ gridColumn: "1 / -1" }}>
            <label style={{ fontSize: 11, color: theme.color.subtle, display: "block", marginBottom: 4 }}>Accepted on — the date the owner accepted the residual risk</label>
            <input type="date" value={accepted} max={todayInput()} onChange={e => setAccepted(e.target.value)} style={{ ...inputStyle, width: "100%", boxSizing: "border-box" }} />
            <div style={{ ...text.hint, marginTop: 4 }}>A risk-accepted vulnerability exception needs an owner and this date (ISO 27001 6.1.3 f).</div>
          </div>
        )}
        {type === "accept" && (
          <>
            <div>
              <label style={{ fontSize: 11, color: theme.color.subtle, display: "block", marginBottom: 4 }}>Approved by</label>
              <input value={approver} onChange={e => setApprover(e.target.value)} placeholder="Someone other than the owner" style={{ ...inputStyle, width: "100%", boxSizing: "border-box" }} />
              {aboveAppetite && approverIsOwner && <div style={{ ...text.hint, marginTop: 4, color: theme.color.dangerText }}>This is the owner: above appetite, someone else must approve.</div>}
            </div>
            <div>
              <label style={{ fontSize: 11, color: theme.color.subtle, display: "block", marginBottom: 4 }}>Acceptance expires</label>
              <input type="date" value={expires} min={accepted || undefined} onChange={e => setExpires(e.target.value)} style={{ ...inputStyle, width: "100%", boxSizing: "border-box" }} />
              {latestExpiry && (
                <div style={{ ...text.hint, marginTop: 4, color: expires && expires > latestExpiry ? theme.color.dangerText : undefined }}>
                  The band allows up to {fmtDateOnly(latestExpiry)}.
                </div>
              )}
            </div>
            <div style={{ gridColumn: "1 / -1", marginTop: -4 }}>
              <AcceptanceHint assessment={appetite} />
            </div>
          </>
        )}
      </div>
      <div style={{ marginBottom: 10 }}>
        <label style={{ fontSize: 11, color: theme.color.subtle, display: "block", marginBottom: 4 }}>Description</label>
        <textarea
          value={desc} onChange={e => setDesc(e.target.value)}
          rows={3} placeholder="Describe the treatment action…"
          style={{ ...inputStyle, width: "100%", boxSizing: "border-box", resize: "vertical" }}
        />
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <button
          onClick={save}
          disabled={saving}
          style={{ ...btnPrimary, fontSize: 12, padding: "6px 14px", opacity: saving ? 0.6 : 1, cursor: saving ? "not-allowed" : "pointer" }}
        >
          {saving ? "Saving…" : "Save treatment"}
        </button>
        <button onClick={onCancel} style={{ fontSize: 12, padding: "6px 14px", border: `1px solid ${theme.color.border}`, borderRadius: 3, background: "transparent", cursor: "pointer", color: theme.color.muted }}>
          Cancel
        </button>
      </div>
    </div>
  );
}

// ── AI evaluation result display ──────────────────────────────────────────

function AiEvaluationResult({ evaluation }: { evaluation: AiEvaluation }) {
  const rowStyle: React.CSSProperties = { display: "grid", gridTemplateColumns: "90px 1fr", gap: "4px 12px", alignItems: "start", marginBottom: 6 };
  const labelStyle: React.CSSProperties = { fontSize: 11, fontWeight: 600, color: theme.color.subtle, paddingTop: 2 };
  const valueStyle: React.CSSProperties = { fontSize: 12, color: theme.color.text, lineHeight: 1.5 };
  const scoreStyle = (score: number): React.CSSProperties => ({
    display: "inline-block", fontWeight: 700, fontSize: 12,
    color: scoreColor(score), padding: "1px 6px", borderRadius: 4,
    background: scoreBg(score),
  });

  return (
    <div style={{ background: theme.color.panelAlt, borderRadius: 3, padding: 12, border: `1px solid ${theme.color.border}` }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: theme.color.subtle, marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.05em" }}>
        Latest AI evaluation · {new Date(evaluation.createdAt).toLocaleDateString()}
      </div>
      <div style={{ marginBottom: 10 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: theme.color.heading, marginBottom: 4 }}>
          Inherent risk &nbsp;<span style={scoreStyle(evaluation.inherentScore)}>{evaluation.inherentScore} ({evaluation.inherentLikelihood}×{evaluation.inherentImpact})</span>
        </div>
        <div style={rowStyle}>
          <span style={labelStyle}>Likelihood</span>
          <span style={valueStyle}>{evaluation.inherentLikelihoodRationale}</span>
        </div>
        <div style={rowStyle}>
          <span style={labelStyle}>Impact</span>
          <span style={valueStyle}>{evaluation.inherentImpactRationale}</span>
        </div>
      </div>
      <div>
        <div style={{ fontSize: 11, fontWeight: 600, color: theme.color.heading, marginBottom: 4 }}>
          Residual risk &nbsp;<span style={scoreStyle(evaluation.residualScore)}>{evaluation.residualScore} ({evaluation.residualLikelihood}×{evaluation.residualImpact})</span>
        </div>
        <div style={rowStyle}>
          <span style={labelStyle}>Likelihood</span>
          <span style={valueStyle}>{evaluation.residualLikelihoodRationale}</span>
        </div>
        <div style={rowStyle}>
          <span style={labelStyle}>Impact</span>
          <span style={valueStyle}>{evaluation.residualImpactRationale}</span>
        </div>
      </div>
    </div>
  );
}

// ── Read-only risk view ───────────────────────────────────────────────────
//
// Opening a risk (a row, the matrix or ?risk=<id>) shows it here first; the form sits behind
// Edit and the state changes (evaluate, confirm or revert a tentative remediation, keep a risk
// whose framework was removed, add a treatment) under Actions (components/RecordView.tsx).

const capitalise = (s: string) => s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, " ");

function scoreValue(l: number | null | undefined, i: number | null | undefined, score: number | null | undefined, overridden: boolean) {
  if (score == null) return null;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
      <ScoreBadge score={score} size="sm" />
      <span>{scoreLabel(score)}{l != null && i != null ? ` (likelihood ${l} × impact ${i})` : ""}{overridden ? ", set by hand" : ""}</span>
    </span>
  );
}

/**
 * What a complete risk record holds (ISO 27001 6.1.2 / 6.1.3, ISO 27005): a named owner, the threat
 * and the weakness it exploits, an inherent rating, a treatment with an owner and a date, a residual
 * rating, the control that treats it and a review date. Closed risks ask nothing more.
 */
function riskSteps(risk: Risk, assessment?: AppetiteAssessment | null): NextStepItem[] {
  if (risk.status === "closed") return [];
  const reviewOverdue = !!risk.reviewDate && risk.reviewDate.slice(0, 10) < todayInput();
  const openTreatments = risk.treatments.filter((t) => t.status !== "completed");
  const treatmentLate = openTreatments.some((t) => !!t.dueDate && t.dueDate.slice(0, 10) < todayInput());
  // Accepting or transferring the risk is itself the treatment decision, even without a treatment row.
  const decided = risk.treatments.length > 0 || risk.status === "accepted" || risk.status === "transferred";
  return [
    { done: !!(risk.ownerUserId || risk.owner), label: "Name the risk owner", hint: "The person accountable for the treatment and the residual risk (ISO 27001 6.1.2 c)." },
    { done: !!risk.threat?.trim() && !!risk.vulnerability?.trim(), label: "Describe the threat and the vulnerability it exploits" },
    { done: !!(risk.assetId || risk.assetOrProcess), label: "Say which asset or process is at risk" },
    { done: risk.inherentLikelihood != null && risk.inherentImpact != null, label: "Rate the inherent likelihood and impact" },
    {
      done: decided, label: "Choose a treatment",
      hint: "Mitigate, accept, transfer or avoid (ISO 27001 6.1.3).",
      urgent: !decided && assessment?.verdict === "above",
    },
    ...(risk.treatments.length > 0 ? [{
      done: risk.treatments.every((t) => !!t.owner && (t.type === "accept" || !!t.dueDate)) && !treatmentLate,
      label: "Give every treatment an owner and a due date",
      hint: treatmentLate ? "A treatment is past its due date." : undefined,
      urgent: treatmentLate,
    }] : []),
    { done: !!risk.linkedControlId, label: "Link the control that treats it", hint: "Ties the risk to the Statement of Applicability." },
    { done: risk.residualLikelihood != null && risk.residualImpact != null, label: "Rate the residual likelihood and impact" },
    ...(assessment?.accepted && assessment.verdict === "above" ? [{
      done: assessment.acceptanceIssues.length === 0, label: "Have the acceptance approved, with an expiry",
      hint: "Above appetite: approved by someone other than the owner, for a limited time.", urgent: assessment.acceptanceIssues.length > 0,
    }] : []),
    { done: !!risk.reviewDate && !reviewOverdue, label: reviewOverdue ? "Review the risk (the review date has passed)" : "Set a review date", urgent: reviewOverdue },
  ];
}

function RiskDetailsView({ risk, crossRefs, appetite }:{ risk: Risk; crossRefs?: CrossRefsData | null; appetite?: RiskAppetiteView | null }) {
  const overdueReview = isOverdueReview(risk);
  const effScore = risk.residualScore ?? risk.inherentScore ?? null;
  const months = reviewIntervalMonths(effScore);
  const cadence = months === 1 ? "Monthly" : months === 3 ? "Quarterly" : "Annual";
  const aboveTarget = risk.targetScore != null && risk.residualScore != null && risk.residualScore > risk.targetScore;
  const links: RelatedLink[] = [
    ...(risk.asset ? [{ kindLabel: "Asset", label: risk.asset.name, href: KIND_HREFS.asset(risk.asset.id) }] : []),
    ...(risk.site ? [{ kindLabel: "Site", label: `${risk.site.name} · ${risk.site.country}`, href: `/sites/${encodeURIComponent(risk.site.id)}` }] : []),
    ...(risk.linkedControl ? [{ kindLabel: FRAMEWORK_LABELS[risk.linkedControl.frameworkId as FrameworkId] ?? "Control", label: `${controlRef(risk.linkedControl.frameworkId, risk.linkedControl.id)} ${risk.linkedControl.name}`, href: null }] : []),
    ...(risk.insurancePolicy ? [{ kindLabel: "Insurance", label: risk.insurancePolicy.insurer, href: `/resilience/insurance?policy=${encodeURIComponent(risk.insurancePolicy.id)}` }] : []),
    ...(crossRefs ? moduleLinkList(crossRefs) : []),
  ];
  return (
    <div>
      <DetailSection title="Details">
        <DetailFields fields={[
          { label: "Category", value: risk.category },
          { label: "Status", value: <StatusPill status={risk.status} /> },
          { label: "Priority", value: risk.priority ? (
            <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 4, fontWeight: 600, background: priorityBg(risk.priority), color: priorityColor(risk.priority) }}>{capitalise(risk.priority)}</span>
          ) : null },
          { label: "Next review", value: risk.reviewDate ? (
            <span style={{ color: overdueReview ? theme.color.dangerText : undefined, fontWeight: overdueReview ? 600 : undefined }}>
              {fmtDateOnly(risk.reviewDate)}{overdueReview ? " (overdue)" : ""}
            </span>
          ) : null },
          { label: "Threat", value: risk.threat, wide: true, multiline: true },
          { label: "Vulnerability", value: risk.vulnerability, wide: true, multiline: true },
          { label: "Asset or process at risk", value: risk.assetOrProcess },
          { label: "Site", value: risk.site ? `${risk.site.name} · ${risk.site.country}` : "Organisation-wide" },
          { label: "Risk owner", value: risk.owner },
          ...(risk.insurancePolicy ? [{ label: "Transferred to", value: `${risk.insurancePolicy.insurer}${risk.insurancePolicy.policyNumber ? ` (${risk.insurancePolicy.policyNumber})` : ""}` }] : []),
          { label: "Assurance / oversight owner", value: risk.assuranceOwner },
          { label: "Applicable frameworks", value: risk.frameworkIds.length ? (
            <span style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
              {risk.frameworkIds.map((f) => (
                <span key={f} style={{ fontSize: 11, padding: "1px 8px", borderRadius: 4, background: theme.color.accentBg, color: theme.color.accentText }}>{FRAMEWORK_LABELS[f as FrameworkId] ?? f}</span>
              ))}
            </span>
          ) : null, wide: true },
          { label: "Linked control", value: risk.linkedControl ? `${FRAMEWORK_LABELS[risk.linkedControl.frameworkId as FrameworkId] ?? risk.linkedControl.frameworkId} ${controlRef(risk.linkedControl.frameworkId, risk.linkedControl.id)}: ${risk.linkedControl.name}` : null, wide: true },
        ]} />
        <EntityTagsRow entity={{ kind: "risk", id: risk.id }} style={{ marginTop: 12, marginBottom: 0 }} />
      </DetailSection>

      <DetailSection title="Scoring">
        <div style={{ display: "grid", gap: 12 }}>
          <DetailFields fields={[
            { label: "Inherent risk (before treatment)", value: scoreValue(risk.inherentLikelihood, risk.inherentImpact, risk.inherentScore, risk.inherentOverridden) },
            { label: "Residual risk (after treatment)", value: scoreValue(risk.residualLikelihood, risk.residualImpact, risk.residualScore, risk.residualOverridden) },
            { label: "Target score", value: risk.targetScore != null ? (
              <span>
                {risk.targetScore} ({scoreLabel(risk.targetScore)})
                {risk.residualScore != null && (
                  <span style={{ marginLeft: 8, fontWeight: 600, color: aboveTarget ? theme.color.dangerText : theme.color.successText }}>
                    {aboveTarget ? `Residual ${risk.residualScore} is above target` : `Residual ${risk.residualScore} meets target`}
                  </span>
                )}
              </span>
            ) : null },
            { label: "Recommended review cadence", value: effScore ? `${cadence}, as a ${scoreLabel(effScore).toLowerCase()} risk` : "Score this risk for a recommendation" },
            ...(appetite ? [{
              label: "Risk appetite",
              value: <AppetiteVerdict assessment={appetite.assessments[risk.id]} view={appetite} category={risk.category} hasResidual={risk.residualScore != null} />,
              wide: true,
            }] : []),
          ]} />
          {risk.evaluations?.[0] && <AiEvaluationResult evaluation={risk.evaluations[0]} />}
        </div>
      </DetailSection>

      {/* The asset, site and control it names, and whatever shares its tags */}
      <DetailSection title="Linked records">
        <RelatedRecords entity={{ kind: "risk", id: risk.id }} links={links} />
      </DetailSection>

      <DetailSection title="History">
        <DetailFields fields={[
          { label: "Recorded", value: fmtDate(risk.createdAt) },
          { label: "Last updated", value: fmtDate(risk.updatedAt) },
          { label: "Latest AI evaluation", value: risk.evaluations?.[0] ? fmtDate(risk.evaluations[0].createdAt) : null },
        ]} />
      </DetailSection>
    </div>
  );
}

function TreatmentsView({ risk }: { risk: Risk }) {
  if (risk.treatments.length === 0) {
    return <p style={{ ...text.hint, margin: "0 0 12px" }}>No treatments recorded yet.</p>;
  }
  return (
    <div>
      {risk.treatments.map((t) => {
        const overdue = !!t.dueDate && t.status !== "completed" && new Date(t.dueDate) < new Date();
        return (
          <div key={t.id} style={{ background: theme.color.panelAlt, borderRadius: 3, padding: 12, marginBottom: 8 }}>
            <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", marginBottom: 6 }}>
              <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 4, background: theme.color.accentBg, color: theme.color.accentText, fontWeight: 600 }}>{capitalise(t.type)}</span>
              <StatusTag tone={t.status === "completed" ? "met" : t.status === "in_progress" ? "partial" : "none"}>{capitalise(t.status)}</StatusTag>
              {overdue && <span style={{ fontSize: 10, padding: "2px 6px", borderRadius: 4, background: theme.color.dangerBg, color: theme.color.dangerText, fontWeight: 600 }}>Overdue</span>}
            </div>
            <p style={{ ...text.body, margin: "0 0 4px", whiteSpace: "pre-wrap" }}>{t.description}</p>
            <div style={{ ...text.meta }}>Owner: {t.owner || "—"} · Due: {t.dueDate ? fmtDateOnly(t.dueDate) : "—"}{t.type === "accept" ? ` · ${acceptanceSummary(t)}` : ""}</div>
          </div>
        );
      })}
    </div>
  );
}

// ── Risk modal ────────────────────────────────────────────────────────────

// ── Cross-refs types ──────────────────────────────────────────────────────

interface SoaWarning {
  controlId: string;
  controlName: string;
  status: "GAP" | "NOT_STARTED";
}

interface RelatedRisk {
  id: string;
  displayId?: number | null;
  title: string;
  category: string;
  status: string;
  residualScore?: number | null;
  inherentScore?: number | null;
  linkedControlId?: string | null;
}

interface ConvergingSignal {
  source: string;
  count: number;
  label: string;
}

interface ComplianceGap {
  controlId: string;
  controlName: string;
  status: "GAP" | "PARTIAL";
  gaps: string[];
}

interface RiskDependency {
  id: string;
  sourceRiskId: string;
  targetRiskId: string;
  dependencyType: string;
  note?: string | null;
  sourceRisk?: { id: string; displayId?: number | null; title: string; residualScore?: number | null };
  targetRisk?: { id: string; displayId?: number | null; title: string; residualScore?: number | null };
}

interface CrossRefsData {
  additionalControls?: Array<{ controlId: string; control: { id: string; name: string; frameworkId: string } }>;
  evidenceLinks?: Array<{ documentId: string; addedAt: string; document: { id: string; fileName: string } }>;
  soaWarnings: SoaWarning[];
  complianceGaps: ComplianceGap[];
  relatedRisks: RelatedRisk[];
  convergingSignals: ConvergingSignal[];
  dataBreaches: Array<{ id: string; title: string; severity: string; discoveredAt: string; status: string }>;
  doraIncidents: Array<{ id: string; title: string; isMajor: boolean; detectedAt: string; status: string }>;
  auditorFindings: Array<{ id: string; controlId: string; frameworkId?: string | null; text: string; status: string; createdAt: string }>;
  heatScore?: number;
  dependencySourceOf?: RiskDependency[];
  dependencyTargetOf?: RiskDependency[];
  correlatedGroups?: CorrelatedGroup[];
  linkedSignals?: SignalRecord[];
  signalCandidates?: SignalRecord[];
  // The platform's own registers that name this risk (services/riskLinks.ts loadRiskModuleLinks).
  securityIncidents?: Array<{ id: string; displayId?: number | null; title: string; severity: string; status: string; detectedAt: string; resolvedAt?: string | null }>;
  correctiveActions?: Array<{ id: string; displayId?: number | null; title: string; status: string; priority: string; dueDate?: string | null }>;
  advisoryTriages?: Array<{ id: string; status: string; autoRaised: boolean; itemId: string; title: string }>;
  dpias?: Array<{ id: string; title: string; status: string; residualRisk: string }>;
  quantification?: { method: string; frequencyMin: number; frequencyMode: number; frequencyMax: number; magnitudeMin?: number | null; magnitudeMode?: number | null; magnitudeMax?: number | null; currency: string; bia?: { id: string; name: string } | null } | null;
  vulnerabilities?: Array<{ id: string; cveId: string; title?: string | null; severity?: string | null; resolvedAt?: string | null; exceptionUntil?: string | null; asset: { id: string; name: string } }>;
  aiSystems?: Array<{ id: string; name: string; riskClass: string; lifecycleStage: string }>;
  exposureScenarios?: Array<{ id: string; name: string; kind: string }>;
  insurancePolicy?: { id: string; insurer: string; policyNumber?: string | null; expiresAt: string; status: string } | null;
}

const padRef = (prefix: string, n?: number | null) => (n != null ? `${prefix}-${String(n).padStart(3, "0")}` : prefix);

/** Every record elsewhere on the platform that names this risk, as links for the details view. */
function moduleLinkList(x: CrossRefsData): RelatedLink[] {
  return [
    ...(x.securityIncidents ?? []).map((i) => ({ kindLabel: "Incident", label: `${padRef("INC", i.displayId)} ${i.title} (${i.status})`, href: KIND_HREFS.incident(i.id) })),
    ...(x.correctiveActions ?? []).map((a) => ({ kindLabel: "Corrective action", label: `${padRef("CA", a.displayId)} ${a.title} (${a.status.replace(/_/g, " ")})`, href: KIND_HREFS.action(a.id) })),
    ...(x.vulnerabilities ?? []).map((v) => ({ kindLabel: "Vulnerability", label: `${v.cveId} on ${v.asset.name}${v.resolvedAt ? " (resolved)" : v.exceptionUntil ? " (risk-accepted)" : ""}`, href: `/vulnerabilities?cve=${encodeURIComponent(v.cveId)}` })),
    ...(x.advisoryTriages ?? []).map((t) => ({ kindLabel: "Advisory", label: `${t.title} (${t.status.replace(/_/g, " ")})`, href: "/threat-intelligence" })),
    ...(x.dpias ?? []).map((d) => ({ kindLabel: "DPIA", label: `${d.title} (residual ${d.residualRisk})`, href: KIND_HREFS.dpia(d.id) })),
    ...(x.aiSystems ?? []).map((s) => ({ kindLabel: "AI system", label: `${s.name} (${s.riskClass})`, href: KIND_HREFS.ai_system(s.id) })),
    ...(x.exposureScenarios ?? []).map((s) => ({ kindLabel: "Exposure scenario", label: s.name, href: "/exposure" })),
  ];
}

/** The figures a FAIR-style quantification puts on the risk (Exposure page), read-only here. */
function QuantificationSummary({ q }: { q: NonNullable<CrossRefsData["quantification"]> }) {
  const money = (n?: number | null) => (n == null ? "—" : new Intl.NumberFormat("en-GB", { style: "currency", currency: q.currency || "GBP", maximumFractionDigits: 0 }).format(n));
  return (
    <div style={{ background: theme.color.panelAlt, borderRadius: 3, padding: "12px 16px" }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: theme.color.heading, marginBottom: 6 }}>Quantified exposure</div>
      <div style={{ fontSize: 12, color: theme.color.muted, display: "grid", gap: 3 }}>
        <span>Events a year: {q.frequencyMin} – {q.frequencyMode} – {q.frequencyMax} (min – most likely – max)</span>
        <span>Loss per event: {q.method === "bia" ? `from ${q.bia?.name ?? "a business impact analysis"}` : `${money(q.magnitudeMin)} – ${money(q.magnitudeMode)} – ${money(q.magnitudeMax)}`}</span>
      </div>
      <PortalLink to="/exposure" style={{ fontSize: 11, color: theme.color.accentText }}>Open the exposure model</PortalLink>
    </div>
  );
}

/** A connector record (Sentinel incident, Defender alert, AWS / GCP finding) a risk can be linked to. */
interface SignalRecord {
  table: "sentinel" | "m365alert" | "aws" | "gcp";
  externalId: string;
  title: string;
  severity: string;
  status: string;
  occurredAt: string;
  linkedElsewhere: boolean;
}

const SIGNAL_TABLE_LABEL: Record<SignalRecord["table"], string> = {
  sentinel: "Sentinel", m365alert: "Defender / M365", aws: "AWS Security Hub", gcp: "GCP SCC",
};
const signalKey = (s: SignalRecord) => `${s.table}:${s.externalId}`;

const LINK_BTN: React.CSSProperties = { fontSize: 11, background: "none", border: "none", cursor: "pointer", padding: 0 };
const DEPENDENCY_TYPES = [
  { id: "amplifies", label: "Amplifies" },
  { id: "enables", label: "Enables" },
  { id: "mitigatedBy", label: "Is mitigated by" },
] as const;

/**
 * The risk's own links — further controls, the evidence that bears on it and the risks it
 * amplifies or enables — each added or removed here. The server scopes every id to the
 * organisation and audits each change; without risks:write the lists are read-only.
 */
function RiskLinksPanel({ riskId, crossRefs, canEdit, onChanged }: {
  riskId: string; crossRefs: CrossRefsData; canEdit: boolean; onChanged: () => Promise<void>;
}) {
  const [adding, setAdding] = useState<null | "control" | "evidence" | "dependency" | "signal">(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [docs, setDocs] = useState<Array<{ id: string; fileName: string }> | null>(null);
  const [risks, setRisks] = useState<Array<{ id: string; displayId?: number | null; title: string }> | null>(null);
  const [docId, setDocId] = useState("");
  const [target, setTarget] = useState("");
  const [depType, setDepType] = useState<string>("amplifies");
  const [note, setNote] = useState("");
  const [signal, setSignal] = useState("");

  const controls = crossRefs.additionalControls ?? [];
  const linkedSignals = crossRefs.linkedSignals ?? [];
  const signalCandidates = crossRefs.signalCandidates ?? [];
  const evidence = crossRefs.evidenceLinks ?? [];
  const deps = crossRefs.dependencySourceOf ?? [];

  async function run(fn: () => Promise<unknown>, fallback: string) {
    setBusy(true); setError(null);
    try { await fn(); setAdding(null); setDocId(""); setTarget(""); setNote(""); setSignal(""); await onChanged(); }
    catch (e) { setError(apiErrorMessage(e, fallback)); }
    finally { setBusy(false); }
  }
  function open(kind: "control" | "evidence" | "dependency" | "signal") {
    setAdding(kind); setError(null);
    if (kind === "evidence" && !docs) api.get<{ documents: Array<{ id: string; fileName: string }> }>("/documents").then((d) => setDocs(d.documents)).catch(() => setDocs([]));
    if (kind === "dependency" && !risks) api.get<{ risks: Array<{ id: string; displayId?: number | null; title: string }> }>("/risks").then((d) => setRisks(d.risks.filter((r) => r.id !== riskId))).catch(() => setRisks([]));
  }
  const ref = (r: { displayId?: number | null }) => (r.displayId ? `RISK-${String(r.displayId).padStart(3, "0")}` : "—");
  const row: React.CSSProperties = { display: "flex", alignItems: "center", gap: 8, fontSize: 11, marginBottom: 4 };
  const addBtn = (kind: "control" | "evidence" | "dependency" | "signal", label: string) =>
    canEdit && adding !== kind ? <button type="button" onClick={() => open(kind)} style={{ ...LINK_BTN, color: theme.color.accent }}>+ {label}</button> : null;
  const formButtons = (onSave: () => void, disabled: boolean) => (
    <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
      <button type="button" onClick={onSave} disabled={busy || disabled} style={{ ...btnPrimary, fontSize: 12, padding: "5px 12px", opacity: busy || disabled ? 0.6 : 1 }}>{busy ? "Saving…" : "Add"}</button>
      <button type="button" onClick={() => setAdding(null)} disabled={busy} style={{ ...LINK_BTN, fontSize: 12, color: theme.color.muted }}>Cancel</button>
    </div>
  );

  return (
    <div style={{ background: theme.color.panelAlt, borderRadius: 3, padding: "12px 16px", display: "grid", gap: 12 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: theme.color.heading }}>Links</div>

      <div>
        <div style={{ ...row, justifyContent: "space-between" }}><span style={{ fontWeight: 600, color: theme.color.muted }}>Further controls ({controls.length})</span>{addBtn("control", "Link a control")}</div>
        {controls.map((c) => (
          <div key={c.controlId} style={row}>
            <span style={{ fontVariantNumeric: "tabular-nums", color: theme.color.subtle, minWidth: 68 }}>{controlRef(c.control.frameworkId, c.controlId)}</span>
            <span style={{ flex: 1, color: theme.color.text }}>{c.control.name}</span>
            {canEdit && <button type="button" disabled={busy} onClick={() => run(() => api.del(`/risks/${riskId}/controls/${encodeURIComponent(c.controlId)}`), "Could not remove the control")} style={{ ...LINK_BTN, color: theme.color.danger }}>Remove</button>}
          </div>
        ))}
        {adding === "control" && (
          <ControlPicker value={null} onChange={(next) => { if (next.controlId) void run(() => api.post(`/risks/${riskId}/controls`, { controlId: next.controlId }), "Could not link the control"); }} />
        )}
      </div>

      <div>
        <div style={{ ...row, justifyContent: "space-between" }}><span style={{ fontWeight: 600, color: theme.color.muted }}>Evidence ({evidence.length})</span>{addBtn("evidence", "Link evidence")}</div>
        {evidence.map((e) => (
          <div key={e.documentId} style={row}>
            <PortalLink to={KIND_HREFS.document(e.documentId) ?? "/documents"} style={{ flex: 1, color: theme.color.accentText }}>{e.document.fileName}</PortalLink>
            {canEdit && <button type="button" disabled={busy} onClick={() => run(() => api.del(`/risks/${riskId}/evidence/${e.documentId}`), "Could not remove the evidence link")} style={{ ...LINK_BTN, color: theme.color.danger }}>Remove</button>}
          </div>
        ))}
        {adding === "evidence" && (
          <div>
            <select value={docId} onChange={(e) => setDocId(e.target.value)} style={{ ...inputStyle, fontSize: 12 }}>
              <option value="">{docs ? "Choose a document…" : "Loading documents…"}</option>
              {(docs ?? []).filter((d) => !evidence.some((e) => e.documentId === d.id)).map((d) => <option key={d.id} value={d.id}>{d.fileName}</option>)}
            </select>
            {formButtons(() => run(() => api.post(`/risks/${riskId}/evidence`, { documentId: docId }), "Could not link the evidence"), !docId)}
          </div>
        )}
      </div>

      <div>
        <div style={{ ...row, justifyContent: "space-between" }}><span style={{ fontWeight: 600, color: theme.color.muted }}>This risk amplifies, enables or is mitigated by ({deps.length})</span>{addBtn("dependency", "Link a risk")}</div>
        {deps.map((d) => (
          <div key={d.id} style={row}>
            <span style={{ fontVariantNumeric: "tabular-nums", color: theme.color.subtle, minWidth: 68 }}>{d.targetRisk ? ref(d.targetRisk) : "—"}</span>
            <span style={{ flex: 1, color: theme.color.text }}>{d.targetRisk?.title ?? d.targetRiskId}</span>
            <span style={{ fontSize: 10, padding: "1px 5px", borderRadius: 4, background: theme.color.consultantBg, color: theme.color.consultant, fontWeight: 600 }}>{DEPENDENCY_TYPES.find((t) => t.id === d.dependencyType)?.label ?? d.dependencyType}</span>
            {canEdit && <button type="button" disabled={busy} onClick={() => run(() => api.del(`/risks/${riskId}/dependencies/${d.id}`), "Could not remove the link")} style={{ ...LINK_BTN, color: theme.color.danger }}>Remove</button>}
          </div>
        ))}
        {adding === "dependency" && (
          <div style={{ display: "grid", gap: 6 }}>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              <select value={depType} onChange={(e) => setDepType(e.target.value)} style={{ ...inputStyle, fontSize: 12, width: "auto" }}>
                {DEPENDENCY_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
              <select value={target} onChange={(e) => setTarget(e.target.value)} style={{ ...inputStyle, fontSize: 12, flex: 1, minWidth: 180 }}>
                <option value="">{risks ? "Choose a risk…" : "Loading risks…"}</option>
                {(risks ?? []).map((r) => <option key={r.id} value={r.id}>{ref(r)} {r.title}</option>)}
              </select>
            </div>
            <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} placeholder="Note (optional)" style={{ ...inputStyle, fontSize: 12 }} />
            {formButtons(() => run(() => api.post(`/risks/${riskId}/dependencies`, { targetRiskId: target, dependencyType: depType, ...(note.trim() ? { note: note.trim() } : {}) }), "Could not link the risk"), !target)}
          </div>
        )}
      </div>
      {(linkedSignals.length > 0 || signalCandidates.length > 0) && (
        <div>
          <div style={{ ...row, justifyContent: "space-between" }}><span style={{ fontWeight: 600, color: theme.color.muted }}>Connector records ({linkedSignals.length})</span>{signalCandidates.length > 0 && addBtn("signal", "Link a record")}</div>
          {linkedSignals.map((sig) => (
            <div key={signalKey(sig)} style={row}>
              <span style={{ fontSize: 10, padding: "1px 5px", borderRadius: 4, background: theme.color.accentBg, color: theme.color.accentText, fontWeight: 600, whiteSpace: "nowrap" }}>{SIGNAL_TABLE_LABEL[sig.table]}</span>
              <span style={{ flex: 1, color: theme.color.text }}>{sig.title}</span>
              <span style={{ color: theme.color.subtle }}>{sig.severity} · {sig.status}</span>
              {canEdit && <button type="button" disabled={busy} onClick={() => run(() => api.patch(`/risks/${riskId}/signal-link`, { table: sig.table, externalId: sig.externalId, unlink: true }), "Could not unlink the record")} style={{ ...LINK_BTN, color: theme.color.danger }}>Unlink</button>}
            </div>
          ))}
          {adding === "signal" && (
            <div>
              <select value={signal} onChange={(e) => setSignal(e.target.value)} style={{ ...inputStyle, fontSize: 12 }}>
                <option value="">Choose an open record…</option>
                {signalCandidates.map((sig) => (
                  <option key={signalKey(sig)} value={signalKey(sig)}>
                    {SIGNAL_TABLE_LABEL[sig.table]}: {sig.title} ({sig.severity}){sig.linkedElsewhere ? " — linked to another risk" : ""}
                  </option>
                ))}
              </select>
              {formButtons(() => {
                const sig = signalCandidates.find((c) => signalKey(c) === signal);
                if (sig) void run(() => api.patch(`/risks/${riskId}/signal-link`, { table: sig.table, externalId: sig.externalId }), "Could not link the record");
              }, !signal)}
            </div>
          )}
        </div>
      )}
      {error && <div style={{ fontSize: 12, color: theme.color.dangerText }}>{error}</div>}
    </div>
  );
}

function RiskModal({
  risk, canEdit, onClose, onSaved, appetite,
}: {
  risk: Risk | null;
  /** risks:write, which every write under /risks needs; without it the risk is only viewed. */
  canEdit: boolean;
  /** Every risk's verdict against the risk appetite (GET /risks/appetite), refreshed by the page. */
  appetite?: RiskAppetiteView | null;
  onClose: () => void;
  onSaved: (r: Risk) => void;
}) {
  const { frameworks } = useFrameworks();
  const isNew = !risk;
  // An existing risk opens read-only; the form is behind Edit. A new one starts in the form.
  const [mode, setMode] = useState<"view" | "edit">(isNew ? "edit" : "view");
  const editing = mode === "edit" && (isNew || canEdit);

  const [tab, setTab] = useState<"details" | "treatments" | "signals">("details");
  const [saving, setSaving] = useState(false);
  const [addingTreatment, setAddingTreatment] = useState(false);
  const [editingTid, setEditingTid] = useState<string | null>(null);
  const [deletingTid, setDeletingTid] = useState<string | null>(null);
  const [deletingRisk, setDeletingRisk] = useState(false);
  const [localRisk, setLocalRisk] = useState<Risk | null>(risk);
  // The risk as it stands after the last write, for handlers that build on it after an await.
  const latestRisk = useRef<Risk | null>(risk);
  latestRisk.current = localRisk;

  const [crossRefs, setCrossRefs] = useState<CrossRefsData | null>(null);
  const [crossRefsLoading, setCrossRefsLoading] = useState(false);

  const [evalRunId, setEvalRunId] = useState<string | null>(null);
  const [evalStatus, setEvalStatus] = useState<"idle" | "queued" | "running" | "done" | "error">("idle");
  const [evalError, setEvalError] = useState<string | null>(null);
  const evalPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const evalPollFailuresRef = useRef(0);
  const evalPollStartRef = useRef<number>(0);

  async function reloadCrossRefs() {
    if (!risk?.id) return;
    setCrossRefs(await api.get<CrossRefsData>(`/risks/${risk.id}/cross-refs`));
  }

  useEffect(() => {
    if (!risk?.id) return;
    setCrossRefsLoading(true);
    api.get<CrossRefsData>(`/risks/${risk.id}/cross-refs`)
      .then(setCrossRefs)
      .catch(() => {})
      .finally(() => setCrossRefsLoading(false));
  }, [risk?.id]);

  useEffect(() => {
    if (!evalRunId || evalStatus === "done" || evalStatus === "error" || evalStatus === "idle") return;
    evalPollRef.current = setInterval(async () => {
      if (Date.now() - evalPollStartRef.current > 5 * 60 * 1000) {
        clearInterval(evalPollRef.current!);
        setEvalStatus("error");
        setEvalError("AI evaluation timed out. Please try again.");
        return;
      }
      try {
        const res = await api.get<{ status: string; errorMessage?: string }>(`/risks/evaluations/${evalRunId}`);
        evalPollFailuresRef.current = 0;
        if (res.status === "done") {
          clearInterval(evalPollRef.current!);
          setEvalStatus("done");
          if (localRisk) {
            const updated = await api.get<{ risk: Risk }>(`/risks/${localRisk.id}`);
            setLocalRisk(updated.risk);
            onSaved(updated.risk);
          }
        } else if (res.status === "error") {
          clearInterval(evalPollRef.current!);
          setEvalStatus("error");
          setEvalError(res.errorMessage ?? "Unknown error");
        }
      } catch {
        evalPollFailuresRef.current += 1;
        if (evalPollFailuresRef.current >= 5) {
          clearInterval(evalPollRef.current!);
          setEvalStatus("error");
          setEvalError("Lost connection while waiting for AI evaluation. Please try again.");
        }
      }
    }, 3000);
    return () => { if (evalPollRef.current) clearInterval(evalPollRef.current); };
  }, [evalRunId, evalStatus]);

  async function handleEvaluate() {
    if (!localRisk) return;
    setEvalStatus("queued");
    setEvalError(null);
    evalPollFailuresRef.current = 0;
    evalPollStartRef.current = Date.now();
    try {
      const res = await api.post<{ runId: string }>(`/risks/${localRisk.id}/evaluate`, {});
      setEvalRunId(res.runId);
      setEvalStatus("running");
    } catch (e) {
      setEvalStatus("error");
      setEvalError(apiErrorMessage(e, "The evaluation could not be started"));
    }
  }

  function handleApplyAiScores() {
    if (!localRisk?.evaluations?.[0]) return;
    const ev = localRisk.evaluations[0];
    setIL(ev.inherentLikelihood.toString());
    setII(ev.inherentImpact.toString());
    setRL(ev.residualLikelihood.toString());
    setRI(ev.residualImpact.toString());
  }

  // Form fields
  const [title, setTitle]         = useState(risk?.title ?? "");
  const [category, setCategory]   = useState<typeof CATEGORIES[number]>(
    (risk?.category as typeof CATEGORIES[number]) ?? "Confidentiality"
  );
  const [threat, setThreat]       = useState(risk?.threat ?? "");
  const [vuln, setVuln]           = useState(risk?.vulnerability ?? "");
  const [asset, setAsset]         = useState(risk?.assetOrProcess ?? "");
  const [owner, setOwner]         = useState(risk?.owner ?? "");
  const [ownerUserId, setOwnerUserId] = useState<string>(risk?.ownerUserId ?? "");
  const [memberOptions, setMemberOptions] = useState<Array<{ id: string; name: string }>>([]);
  const [assurance, setAssurance] = useState(risk?.assuranceOwner ?? "");
  const [targetScr, setTargetScr] = useState<string>(risk?.targetScore?.toString() ?? "");
  const [priority, setPriority]   = useState<string>(risk?.priority ?? "");
  const [reviewDate, setReviewDate] = useState(toDateInput(risk?.reviewDate));
  const [status, setStatus]       = useState(risk?.status ?? "open");
  const [fwIds, setFwIds]         = useState<string[]>(risk?.frameworkIds ?? []);
  const [linkedControlId, setLinkedControlId] = useState<string | null>(risk?.linkedControlId ?? null);
  const [linkedFw,        setLinkedFw]        = useState<string>(risk?.linkedControl?.frameworkId ?? "");
  const [linkedGroup,     setLinkedGroup]     = useState<string>(
    risk?.linkedControl ? controlGroup(risk.linkedControl.frameworkId, risk.linkedControl.id) : ""
  );
  const [controlOptions, setControlOptions]   = useState<Array<{id: string; name: string; frameworkId: string}>>([]);
  const [siteId, setSiteId]       = useState<string>(risk?.siteId ?? "");
  const [siteOptions, setSiteOptions] = useState<Array<{ id: string; name: string; country: string }>>([]);
  const [assetId, setAssetId]     = useState<string>(risk?.assetId ?? risk?.asset?.id ?? "");
  const [assetOptions, setAssetOptions] = useState<Array<{ id: string; name: string; criticality: string; status?: string }>>([]);
  const [assetImpact, setAssetImpact] = useState<{ impact: number; basis: string[] } | null>(null);
  const [insurancePolicyId, setInsurancePolicyId] = useState<string>(risk?.insurancePolicyId ?? "");
  const [insuranceOptions, setInsuranceOptions] = useState<Array<{ id: string; insurer: string; policyNumber?: string | null; status: string }>>([]);
  const [iL, setIL]               = useState<string>(risk?.inherentLikelihood?.toString() ?? "");
  const [iI, setII]               = useState<string>(risk?.inherentImpact?.toString() ?? "");
  const [rL, setRL]               = useState<string>(risk?.residualLikelihood?.toString() ?? "");
  const [rI, setRI]               = useState<string>(risk?.residualImpact?.toString() ?? "");

  // Opening the form always starts from the record as it now stands.
  function startEditing(onTab: "details" | "treatments" = "details") {
    const r = localRisk;
    if (r) {
      setTitle(r.title); setCategory(r.category as typeof CATEGORIES[number]); setThreat(r.threat);
      setVuln(r.vulnerability ?? ""); setAsset(r.assetOrProcess ?? ""); setOwner(r.owner ?? ""); setOwnerUserId(r.ownerUserId ?? "");
      setAssurance(r.assuranceOwner ?? ""); setTargetScr(r.targetScore?.toString() ?? ""); setPriority(r.priority ?? "");
      setReviewDate(toDateInput(r.reviewDate)); setStatus(r.status); setFwIds(r.frameworkIds ?? []);
      setLinkedControlId(r.linkedControlId ?? null); setLinkedFw(r.linkedControl?.frameworkId ?? "");
      setLinkedGroup(r.linkedControl ? controlGroup(r.linkedControl.frameworkId, r.linkedControl.id) : "");
      setSiteId(r.siteId ?? "");
      setAssetId(r.assetId ?? r.asset?.id ?? ""); setInsurancePolicyId(r.insurancePolicyId ?? "");
      setIL(r.inherentLikelihood?.toString() ?? ""); setII(r.inherentImpact?.toString() ?? "");
      setRL(r.residualLikelihood?.toString() ?? ""); setRI(r.residualImpact?.toString() ?? "");
    }
    setAddingTreatment(false); setEditingTid(null); setDeletingTid(null); setDeletingRisk(false); setWriteError(null);
    setTab(onTab);
    setMode("edit");
  }

  function cancelEditing() {
    if (isNew) { onClose(); return; }
    setAddingTreatment(false); setEditingTid(null); setDeletingTid(null); setDeletingRisk(false);
    setMode("view");
  }

  // A score is a whole number from 1 to 5; anything else shows no product rather than "0" (DOC-4).
  const validScore = (v: string) => /^[1-5]$/.test(v.trim());
  const iScore = validScore(iL) && validScore(iI) ? parseInt(iL) * parseInt(iI) : null;
  const rScore = validScore(rL) && validScore(rI) ? parseInt(rL) * parseInt(rI) : null;

  function toggleFw(id: string) {
    setFwIds(prev => prev.includes(id) ? prev.filter(f => f !== id) : [...prev, id]);
  }

  useEffect(() => {
    api.get<{ soa: Array<{ control: { id: string; name: string; frameworkId: string } }> }>("/controls/soa")
      .then(d => setControlOptions(d.soa.map(r => ({ id: r.control.id, name: r.control.name, frameworkId: r.control.frameworkId }))));
    api.get<{ sites: Array<{ id: string; name: string; country: string }> }>("/sites")
      .then(d => setSiteOptions(d.sites)).catch(() => {});
    api.get<{ assets: Array<{ id: string; name: string; criticality: string; status?: string }> }>("/resilience/assets")
      .then(d => setAssetOptions(d.assets.filter((a) => a.status !== "retired"))).catch(() => {});
    api.get<{ policies: Array<{ id: string; insurer: string; policyNumber?: string | null; status: string }> }>("/resilience/insurance")
      .then(d => setInsuranceOptions(d.policies)).catch(() => {});
    api.get<{ members: Array<{ id: string; displayName: string | null; email: string }> }>("/organizations/members")
      .then(d => setMemberOptions(d.members.map(m => ({ id: m.id, name: m.displayName || m.email })))).catch(() => {});
  }, []);

  // What the asset register and the business impact analyses say the impact should be. A
  // suggestion: it fills nothing until the member applies it.
  useEffect(() => {
    if (!assetId) { setAssetImpact(null); return; }
    let live = true;
    api.get<{ impact: number; basis: string[] }>(`/risks/asset-impact?assetId=${encodeURIComponent(assetId)}`)
      .then((d) => { if (live) setAssetImpact(d); })
      .catch(() => { if (live) setAssetImpact(null); });
    return () => { live = false; };
  }, [assetId]);
  const transferred = status === "transferred" || (localRisk?.treatments ?? []).some((t) => t.type === "transfer");

  // What the server refused on the last write (a link to another tenant's row, a policy on a risk
  // that is not transferred, an acceptance a vulnerability exception rests on), shown in the footer
  // rather than lost as an unhandled rejection.
  const [writeError, setWriteError] = useState<string | null>(null);
  async function guarded(fn: () => Promise<void>, fallback: string) {
    setWriteError(null);
    try { await fn(); } catch (e) { setWriteError(apiErrorMessage(e, fallback)); }
  }

  async function handleSave() {
    if (!title.trim() || !threat.trim()) return;
    const badScore = ([["Inherent likelihood", iL], ["Inherent impact", iI], ["Residual likelihood", rL], ["Residual impact", rI]] as const)
      .find(([, v]) => v.trim() !== "" && !validScore(v));
    if (badScore) { setWriteError(`${badScore[0]} must be a whole number from 1 to 5.`); return; }
    setSaving(true);
    setWriteError(null);
    try {
      const body = {
        title: title.trim(), category, threat: threat.trim(),
        // An emptied field is sent as null so the server clears it; undefined would leave the old value.
        vulnerability:  vuln.trim()  || null,
        assetOrProcess: asset.trim() || null,
        owner:          owner.trim() || null,
        // Sent only when a member is chosen or cleared here; otherwise the server matches the text exactly.
        ...(ownerUserId !== (risk?.ownerUserId ?? "") ? { ownerUserId: ownerUserId || null } : {}),
        assuranceOwner: assurance.trim() || null,
        targetScore:    targetScr ? parseInt(targetScr) : null,
        priority:       priority     || null,
        reviewDate:     dateInputToIso(reviewDate),
        status, frameworkIds: fwIds,
        linkedControlId: linkedControlId || null,
        siteId: siteId || null,
        assetId: assetId || null,
        // Only a transferred risk names a policy; the server refuses one on any other.
        insurancePolicyId: transferred ? (insurancePolicyId || null) : null,
        inherentLikelihood: iL ? parseInt(iL) : undefined,
        inherentImpact:     iI ? parseInt(iI) : undefined,
        residualLikelihood: rL ? parseInt(rL) : undefined,
        residualImpact:     rI ? parseInt(rI) : undefined,
        ...(iL || iI ? { inherentOverridden: true } : {}),
        ...(rL || rI ? { residualOverridden: true } : {}),
      };
      const result = isNew
        ? await api.post<{ risk: Risk }>("/risks", body)
        : await api.patch<{ risk: Risk }>(`/risks/${risk!.id}`, body);
      // Back to the view, showing the updated record (a new risk reopens in its view from the page).
      if (!isNew) { setLocalRisk(result.risk); setMode("view"); }
      onSaved(result.risk);
    } catch (e) {
      setWriteError(apiErrorMessage(e, "Could not save the risk"));
    } finally {
      setSaving(false);
    }
  }

  async function handleAddTreatment(t: Omit<Treatment, "id">) {
    if (!localRisk) return;
    await guarded(async () => {
      const result = await api.post<{ treatment: Treatment }>(`/risks/${localRisk.id}/treatments`, t);
      const base = latestRisk.current ?? localRisk;
      const updated = { ...base, treatments: [...base.treatments, result.treatment], status: base.status === "open" ? "under_treatment" : base.status };
      latestRisk.current = updated;
      setLocalRisk(updated);
      onSaved(updated);
      setAddingTreatment(false);
    }, "Could not add the treatment");
  }

  async function handleUpdateTreatment(tid: string, t: Partial<Omit<Treatment, "id">>) {
    if (!localRisk) return;
    await guarded(async () => {
      const result = await api.patch<{ treatment: Treatment }>(`/risks/${localRisk.id}/treatments/${tid}`, t);
      const updated = { ...localRisk, treatments: localRisk.treatments.map(tr => tr.id === tid ? result.treatment : tr) };
      setLocalRisk(updated);
      onSaved(updated);
      setEditingTid(null);
    }, "Could not update the treatment");
  }

  async function handleDeleteTreatment(tid: string) {
    if (!localRisk) return;
    setDeletingTid(null);
    await guarded(async () => {
      await api.del(`/risks/${localRisk.id}/treatments/${tid}`);
      const remaining = localRisk.treatments.filter(tr => tr.id !== tid);
      const updated = { ...localRisk, treatments: remaining };
      setLocalRisk(updated);
      onSaved(updated);
    }, "Could not delete the treatment");
  }

  async function handleDeleteRisk() {
    if (!localRisk) return;
    setDeletingRisk(false);
    await guarded(async () => {
      await api.del(`/risks/${localRisk.id}`);
      onSaved({ ...localRisk, status: "__deleted__" } as Risk);
      onClose();
    }, "Could not delete the risk");
  }

  const scoreField: React.CSSProperties = { ...inputStyle, width: 60, textAlign: "center", boxSizing: "border-box" };
  const labelStyle: React.CSSProperties = { fontSize: 11, color: theme.color.subtle, display: "block", marginBottom: 4 };
  const overdueReview = localRisk ? isOverdueReview(localRisk) : false;

  const evalRunning = evalStatus === "queued" || evalStatus === "running";
  const evalProgress = evalRunning ? (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <div style={{ width: 16, height: 16, border: `2px solid ${theme.color.accent}`, borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.8s linear infinite", flexShrink: 0 }} />
      <span style={{ fontSize: 12, color: theme.color.muted }}>
        {evalStatus === "queued" ? "Queuing AI evaluation…" : "AI is evaluating this risk…"}
      </span>
    </div>
  ) : evalError ? <p style={{ margin: 0, fontSize: 11, color: theme.color.dangerText }}>Error: {evalError}</p> : null;

  // State changes on the record, kept out of the view's reading area.
  async function setRiskFields(patch: Record<string, unknown>) {
    if (!localRisk) return;
    const result = await api.patch<{ risk: Risk }>(`/risks/${localRisk.id}`, patch);
    setLocalRisk(result.risk);
    if (typeof patch.status === "string") setStatus(patch.status);
    onSaved(result.risk);
  }
  const actionBtn: React.CSSProperties = { fontSize: 12, padding: "6px 12px", background: "transparent", color: theme.color.text, border: `1px solid ${theme.color.border}`, borderRadius: 3, cursor: "pointer" };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 1000, display: "flex", alignItems: "flex-start", justifyContent: "flex-end" }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ ...floatingSheet, width: 560, maxWidth: "100vw", height: "100vh", display: "flex", flexDirection: "column", overflowY: "auto" }}>
        {/* Header */}
        <div style={{ padding: "20px 24px 0", borderBottom: "1px solid var(--c-line)", flexShrink: 0 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
            <div>
              {localRisk?.displayId && (
                <div style={{ fontSize: 11, fontWeight: 600, color: theme.color.subtle, marginBottom: 4, fontVariantNumeric: "tabular-nums" }}>
                  RISK-{String(localRisk.displayId).padStart(3, "0")}
                </div>
              )}
              <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: theme.color.heading }}>
                {isNew ? "Add risk" : editing ? `Edit risk: ${localRisk?.title ?? ""}` : localRisk?.title}
              </h2>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              {!isNew && !editing && canEdit && <EditButton onClick={() => startEditing(tab === "treatments" ? "treatments" : "details")} />}
              <button onClick={onClose} aria-label="Close" style={{ background: "none", border: "none", cursor: "pointer", color: theme.color.muted, fontSize: 20, padding: 0, lineHeight: 1 }}>×</button>
            </div>
          </div>
          {!isNew && (
            (() => {
              const signalCount = crossRefs
                ? crossRefs.soaWarnings.length + crossRefs.complianceGaps.length + crossRefs.convergingSignals.length + crossRefs.dataBreaches.length + crossRefs.doraIncidents.length
                : 0;
              const treatmentCount = localRisk ? localRisk.treatments.length : 0;
              return (
                <IndexTabs
                  ground="var(--c-panel)"
                  style={{ marginTop: 8, marginBottom: 0, boxShadow: "none" }}
                  value={tab}
                  onChange={setTab}
                  items={[
                    { id: "details", label: "Details" },
                    { id: "treatments", label: "Treatments", count: treatmentCount > 0 ? treatmentCount : undefined },
                    { id: "signals", label: "Signals", count: signalCount > 0 ? signalCount : undefined },
                  ]}
                />
              );
            })()
          )}
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: 24 }}>

          {!isNew && !editing && localRisk && (
            <NextSteps
              items={riskSteps(localRisk, appetite?.assessments[localRisk.id])}
              action={canEdit ? <button type="button" style={actionBtn} onClick={() => startEditing("details")}>Fill these in</button> : undefined}
            />
          )}

          {/* What is waiting on a decision; the decisions themselves are under Actions */}
          {!isNew && !editing && localRisk?.status === "tentative_remediated" && (
            <div style={{ background: theme.color.auditorBg, border: `1px solid ${theme.color.border}`, borderRadius: 3, padding: "12px 16px", marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: theme.color.auditor, marginBottom: 4 }}>AI found evidence of remediation</div>
              <div style={{ fontSize: 12, color: theme.color.muted }}>
                The AI evaluation detected documented controls that appear to address this risk. Review the evidence, then confirm or revert under Actions.
              </div>
            </div>
          )}

          {!isNew && !editing && localRisk?.suggestedForRemoval && (
            <div style={{ background: theme.color.warningBg, border: `1px solid ${theme.color.border}`, borderRadius: 3, padding: "12px 16px", marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: theme.color.warningText, marginBottom: 4 }}>Framework removed: review this risk</div>
              <div style={{ fontSize: 12, color: theme.color.muted }}>
                This risk was tagged to framework(s) that have since been unenrolled ({localRisk.suggestedForRemovalFrameworks.join(", ")}). You can keep it (Actions), delete it (Edit), or leave this notice.
              </div>
            </div>
          )}

          {!isNew && !editing && tab === "details" && localRisk && <RiskDetailsView risk={localRisk} crossRefs={crossRefs} appetite={appetite} />}
          {!isNew && !editing && tab === "treatments" && localRisk && <TreatmentsView risk={localRisk} />}

          {editing && tab === "details" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>

              {/* ID + Title */}
              <div>
                <label style={labelStyle}>Title *</label>
                <input value={title} maxLength={200} onChange={e => setTitle(e.target.value)} placeholder="e.g. Ransomware attack on file servers" style={{ ...inputStyle, width: "100%", boxSizing: "border-box" }} />
              </div>

              {/* Category + Status */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <div>
                  <label style={labelStyle}>Category *</label>
                  <select value={category} onChange={e => setCategory(e.target.value as typeof CATEGORIES[number])} style={{ ...inputStyle, width: "100%" }}>
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Status</label>
                  <select value={status} onChange={e => setStatus(e.target.value)} style={{ ...inputStyle, width: "100%" }}>
                    {STATUSES.map(s => <option key={s} value={s}>{statusLabel(s)}</option>)}
                  </select>
                </div>
              </div>

              {/* Priority + Review date */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <div>
                  <label style={labelStyle}>Priority</label>
                  <select value={priority} onChange={e => setPriority(e.target.value)} style={{ ...inputStyle, width: "100%" }}>
                    <option value="">— Not set —</option>
                    {PRIORITIES.map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>
                    Next review date
                    {overdueReview && <span style={{ color: theme.color.dangerText, marginLeft: 6 }}><AlertTriangle size={11} style={{ verticalAlign: "-1px", marginRight: 3 }} />Overdue</span>}
                  </label>
                  <input type="date" value={reviewDate} onChange={e => setReviewDate(e.target.value)}
                    style={{ ...inputStyle, width: "100%", boxSizing: "border-box", ...(overdueReview ? { borderColor: theme.color.danger } : {}) }} />
                  {(() => {
                    const effScore = rScore ?? iScore;
                    const months = reviewIntervalMonths(effScore);
                    const cadenceLabel = months === 1 ? "monthly" : months === 3 ? "quarterly" : "annual";
                    return (
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4 }}>
                        <span style={{ fontSize: 10, color: theme.color.subtle }}>
                          {effScore ? `${scoreLabel(effScore)} risk → ${cadenceLabel} review recommended` : "Score this risk for a cadence recommendation"}
                        </span>
                        {effScore != null && (
                          <button
                            onClick={() => {
                              const d = new Date();
                              d.setMonth(d.getMonth() + months);
                              setReviewDate(localDateInput(d));
                            }}
                            style={{ fontSize: 10, padding: "1px 8px", border: `1px solid ${theme.color.border}`, borderRadius: 3, background: "transparent", cursor: "pointer", color: theme.color.accent, fontWeight: 600, whiteSpace: "nowrap" }}
                          >
                            +{months} month{months > 1 ? "s" : ""}
                          </button>
                        )}
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* Threat */}
              <div>
                <label style={labelStyle}>Threat description *</label>
                <textarea value={threat} maxLength={2000} onChange={e => setThreat(e.target.value)} rows={3} placeholder="Describe what could go wrong…" style={{ ...inputStyle, width: "100%", boxSizing: "border-box", resize: "vertical" }} />
              </div>

              {/* Vulnerability */}
              <div>
                <label style={labelStyle}>Vulnerability (optional)</label>
                <input value={vuln} maxLength={2000} onChange={e => setVuln(e.target.value)} placeholder="Weakness that enables this threat" style={{ ...inputStyle, width: "100%", boxSizing: "border-box" }} />
              </div>

              {/* Asset + Owner */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <div>
                  <label style={labelStyle}>Asset or process at risk</label>
                  <input value={asset} maxLength={500} onChange={e => setAsset(e.target.value)} placeholder="e.g. Customer database" style={{ ...inputStyle, width: "100%", boxSizing: "border-box" }} />
                </div>
                {assetOptions.length > 0 && (
                  <div style={{ gridColumn: "1 / -1" }}>
                    <label style={labelStyle}>Asset register entry (optional)</label>
                    <select value={assetId} onChange={e => setAssetId(e.target.value)} style={{ ...inputStyle, width: "100%", boxSizing: "border-box" }}>
                      <option value="">Not linked to an asset</option>
                      {assetOptions.map(a => <option key={a.id} value={a.id}>{a.name} · {a.criticality}</option>)}
                    </select>
                  </div>
                )}
                {transferred && insuranceOptions.length > 0 && (
                  <div style={{ gridColumn: "1 / -1" }}>
                    <label style={labelStyle}>Insurance policy the risk is transferred to (optional)</label>
                    <select value={insurancePolicyId} onChange={e => setInsurancePolicyId(e.target.value)} style={{ ...inputStyle, width: "100%", boxSizing: "border-box" }}>
                      <option value="">No policy named</option>
                      {insuranceOptions.map(p => <option key={p.id} value={p.id}>{p.insurer}{p.policyNumber ? ` (${p.policyNumber})` : ""}{p.status !== "active" ? ` · ${p.status}` : ""}</option>)}
                    </select>
                  </div>
                )}
                <div>
                  <label style={labelStyle}>Risk owner</label>
                  <input value={owner} onChange={e => setOwner(e.target.value)} placeholder="e.g. CISO" style={{ ...inputStyle, width: "100%", boxSizing: "border-box" }} />
                </div>
                <div style={{ gridColumn: "1 / -1" }}>
                  <label style={labelStyle}>Owner is a member (optional)</label>
                  <select
                    value={ownerUserId}
                    onChange={e => {
                      const id = e.target.value;
                      setOwnerUserId(id);
                      const name = memberOptions.find(m => m.id === id)?.name;
                      if (id && name && !owner.trim()) setOwner(name);
                    }}
                    style={{ ...inputStyle, width: "100%", boxSizing: "border-box" }}
                  >
                    <option value="">Not linked to a member</option>
                    {ownerUserId && !memberOptions.some(m => m.id === ownerUserId) && <option value={ownerUserId}>Current owner (no longer active)</option>}
                    {memberOptions.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                  </select>
                  <div style={{ fontSize: 11, color: theme.color.subtle, marginTop: 3 }}>Linking the owner lets a leaver's checklist find the risk. A name or email that matches one member exactly is linked for you.</div>
                </div>
                <div style={{ gridColumn: "1 / -1" }}>
                  <label style={labelStyle}>Assurance / oversight owner (optional)</label>
                  <input value={assurance} onChange={e => setAssurance(e.target.value)} placeholder="e.g. Audit Committee — provides second-line oversight" style={{ ...inputStyle, width: "100%", boxSizing: "border-box" }} />
                </div>
                {siteOptions.length > 0 && (
                  <div style={{ gridColumn: "1 / -1" }}>
                    <label style={labelStyle}>Site (optional)</label>
                    <select value={siteId} onChange={e => setSiteId(e.target.value)} style={{ ...inputStyle, width: "100%", boxSizing: "border-box" }}>
                      <option value="">Organisation-wide</option>
                      {siteOptions.map(x => <option key={x.id} value={x.id}>{x.name} · {x.country}</option>)}
                    </select>
                  </div>
                )}
              </div>

              {/* Frameworks */}
              <div>
                <label style={labelStyle}>Applicable frameworks</label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {frameworks.map(f => {
                    const active = fwIds.includes(f.frameworkId);
                    return (
                      <button key={f.id} onClick={() => toggleFw(f.frameworkId)} style={{
                        fontSize: 11, padding: "4px 10px", borderRadius: 3,
                        border: `1px solid ${active ? theme.color.accent : theme.color.border}`,
                        background: active ? theme.color.accentBg : "transparent",
                        color: active ? theme.color.accentText : theme.color.muted,
                        cursor: "pointer",
                      }}>
                        {FRAMEWORK_LABELS[f.frameworkId] ?? f.frameworkId}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Linked control — cascading drill-down */}
              <div>
                <label style={labelStyle}>Linked control (optional)</label>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {/* Level 1: Standard */}
                  <select
                    value={linkedFw}
                    onChange={e => { setLinkedFw(e.target.value); setLinkedGroup(""); setLinkedControlId(null); }}
                    style={{ ...inputStyle, width: "100%" }}
                  >
                    <option value="">— Select standard —</option>
                    {[...new Set(controlOptions.map(c => c.frameworkId))].map(fwId => (
                      <option key={fwId} value={fwId}>{FRAMEWORK_LABELS[fwId as FrameworkId] ?? fwId}</option>
                    ))}
                  </select>

                  {/* Level 2: Reference number (section/annex/requirement) */}
                  {linkedFw && (() => {
                    const groups: string[] = [];
                    const seen = new Set<string>();
                    for (const c of controlOptions) {
                      if (c.frameworkId !== linkedFw) continue;
                      const g = controlGroup(c.frameworkId, c.id);
                      if (!seen.has(g)) { seen.add(g); groups.push(g); }
                    }
                    return (
                      <select
                        value={linkedGroup}
                        onChange={e => { setLinkedGroup(e.target.value); setLinkedControlId(null); }}
                        style={{ ...inputStyle, width: "100%" }}
                      >
                        <option value="">— Select reference —</option>
                        {groups.map(g => (
                          <option key={g} value={g}>{groupLabel(linkedFw, g)}</option>
                        ))}
                      </select>
                    );
                  })()}

                  {/* Level 3: Specific control */}
                  {linkedGroup && (() => {
                    const filtered = controlOptions.filter(
                      c => c.frameworkId === linkedFw && controlGroup(c.frameworkId, c.id) === linkedGroup
                    );
                    return (
                      <select
                        value={linkedControlId ?? ""}
                        onChange={e => setLinkedControlId(e.target.value || null)}
                        style={{ ...inputStyle, width: "100%" }}
                      >
                        <option value="">— Select control —</option>
                        {filtered.map(c => (
                          <option key={c.id} value={c.id}>
                            {controlRef(c.frameworkId, c.id)} — {c.name}
                          </option>
                        ))}
                      </select>
                    );
                  })()}

                  {/* Clear link */}
                  {linkedFw && (
                    <button
                      type="button"
                      onClick={() => { setLinkedFw(""); setLinkedGroup(""); setLinkedControlId(null); }}
                      style={{ background: "none", border: "none", padding: 0, cursor: "pointer", fontSize: 11, color: theme.color.subtle, textAlign: "left", textDecoration: "underline" }}
                    >
                      Clear selection
                    </button>
                  )}
                </div>
              </div>

              {/* Scores */}
              <div>
                <label style={{ ...labelStyle, marginBottom: 8 }}>Risk scores (1 = lowest, 5 = highest)</label>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                  <div style={{ background: theme.color.panelAlt, borderRadius: 3, padding: 12 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: theme.color.heading, marginBottom: 8 }}>
                      Inherent risk <span style={{ fontSize: 10, fontWeight: 400, color: theme.color.subtle }}>(before treatment)</span>
                    </div>
                    <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
                      <div>
                        <span style={{ ...labelStyle, marginBottom: 2 }}>Likelihood</span>
                        <input type="number" min={1} max={5} value={iL} onChange={e => setIL(e.target.value)} style={scoreField} placeholder="1–5" />
                      </div>
                      <div>
                        <span style={{ ...labelStyle, marginBottom: 2 }}>Impact</span>
                        <input type="number" min={1} max={5} value={iI} onChange={e => setII(e.target.value)} style={scoreField} placeholder="1–5" />
                      </div>
                      <div style={{ paddingBottom: 2 }}>
                        <ScoreBadge score={iScore} />
                        <div style={{ fontSize: 10, color: scoreColor(iScore), textAlign: "center", marginTop: 2 }}>{scoreLabel(iScore)}</div>
                      </div>
                    </div>
                  </div>

                  <div style={{ background: theme.color.panelAlt, borderRadius: 3, padding: 12 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: theme.color.heading, marginBottom: 8 }}>
                      Residual risk <span style={{ fontSize: 10, fontWeight: 400, color: theme.color.subtle }}>(after treatment)</span>
                    </div>
                    <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
                      <div>
                        <span style={{ ...labelStyle, marginBottom: 2 }}>Likelihood</span>
                        <input type="number" min={1} max={5} value={rL} onChange={e => setRL(e.target.value)} style={scoreField} placeholder="1–5" />
                      </div>
                      <div>
                        <span style={{ ...labelStyle, marginBottom: 2 }}>Impact</span>
                        <input type="number" min={1} max={5} value={rI} onChange={e => setRI(e.target.value)} style={scoreField} placeholder="1–5" />
                      </div>
                      <div style={{ paddingBottom: 2 }}>
                        <ScoreBadge score={rScore} />
                        <div style={{ fontSize: 10, color: scoreColor(rScore), textAlign: "center", marginTop: 2 }}>{scoreLabel(rScore)}</div>
                      </div>
                    </div>
                  </div>
                </div>

                {assetImpact && (
                  <div style={{ marginTop: 10, background: theme.color.panelAlt, borderRadius: 3, padding: 12 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: theme.color.heading }}>Suggested impact from the asset: {assetImpact.impact} of 5</span>
                      {iI !== String(assetImpact.impact) && (
                        <button type="button" onClick={() => setII(String(assetImpact.impact))} style={{ ...LINK_BTN, fontSize: 12, color: theme.color.accentText }}>Use for inherent impact</button>
                      )}
                    </div>
                    <ul style={{ margin: "6px 0 0", paddingLeft: 18, fontSize: 11, color: theme.color.muted }}>
                      {assetImpact.basis.map((b) => <li key={b}>{b}</li>)}
                    </ul>
                  </div>
                )}

                {/* Target score */}
                <div style={{ marginTop: 10, background: theme.color.panelAlt, borderRadius: 3, padding: 12, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: theme.color.heading }}>
                    Target score <span style={{ fontSize: 10, fontWeight: 400, color: theme.color.subtle }}>(desired residual once treatments complete)</span>
                  </div>
                  <input
                    type="number" min={1} max={25} value={targetScr}
                    onChange={e => setTargetScr(e.target.value)}
                    placeholder="1–25"
                    style={{ ...inputStyle, width: 64, textAlign: "center", boxSizing: "border-box" }}
                  />
                  {targetScr && (() => {
                    const tgt = parseInt(targetScr);
                    if (!tgt || tgt < 1 || tgt > 25) return null;
                    return (
                      <>
                        <span style={{ fontSize: 10, fontWeight: 700, color: scoreColor(tgt), padding: "2px 8px", borderRadius: 4, background: scoreBg(tgt) }}>
                          {scoreLabel(tgt)}
                        </span>
                        {rScore != null && (
                          <span style={{ fontSize: 11, fontWeight: 600, color: rScore > tgt ? theme.color.dangerText : theme.color.successText }}>
                            {rScore > tgt ? `Residual ${rScore} is above target` : `Residual ${rScore} meets target`}
                          </span>
                        )}
                      </>
                    );
                  })()}
                </div>

                {/* AI evaluation: the form only applies the latest scores; running one is under Actions in the view */}
                {!isNew && (
                  <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 6 }}>
                    {localRisk?.evaluations?.[0] && (
                      <div>
                        <button
                          onClick={handleApplyAiScores}
                          style={{ fontSize: 12, padding: "6px 14px", border: `1px solid ${theme.color.border}`, borderRadius: 3, background: "transparent", color: theme.color.muted, cursor: "pointer" }}
                        >
                          {evalStatus === "done" ? "Apply AI scores" : "Apply last AI scores"}
                        </button>
                      </div>
                    )}
                    {evalProgress}
                    {localRisk?.evaluations?.[0] && (
                      <AiEvaluationResult evaluation={localRisk.evaluations[0]} />
                    )}
                  </div>
                )}
                {isNew && (
                  <p style={{ margin: "6px 0 0", fontSize: 11, color: theme.color.subtle }}>
                    Save this risk first, then use Evaluate with AI under Actions to score it automatically.
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Treatments tab, editing */}
          {!isNew && editing && tab === "treatments" && localRisk && (
            <div>
              {localRisk.treatments.length === 0 && !addingTreatment && (
                <p style={{ color: theme.color.subtle, fontSize: 13, margin: "0 0 12px" }}>
                  No treatments added yet. Add one to describe what is being done to reduce this risk.
                </p>
              )}

              {localRisk.treatments.map(t => (
                <div key={t.id} style={{ background: theme.color.panelAlt, borderRadius: 3, padding: 12, marginBottom: 8 }}>
                  {editingTid === t.id ? (
                    <TreatmentForm
                      initial={t}
                      appetite={appetite?.assessments[localRisk.id]}
                      riskOwner={localRisk.owner}
                      onSave={updated => handleUpdateTreatment(t.id, updated)}
                      onCancel={() => setEditingTid(null)}
                    />
                  ) : (
                    <div>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
                        <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                          <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 4, background: theme.color.accentBg, color: theme.color.accentText, fontWeight: 600 }}>
                            {t.type.charAt(0).toUpperCase() + t.type.slice(1)}
                          </span>
                          <StatusTag tone={t.status === "completed" ? "met" : t.status === "in_progress" ? "partial" : "none"}>
                            {t.status.replace("_", " ").replace(/^\w/, c => c.toUpperCase())}
                          </StatusTag>
                          {t.dueDate && t.status !== "completed" && new Date(t.dueDate) < new Date() && (
                            <span style={{ fontSize: 10, padding: "2px 6px", borderRadius: 4, background: theme.color.dangerBg, color: theme.color.dangerText, fontWeight: 600 }}>
                              Overdue
                            </span>
                          )}
                        </div>
                        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                          {deletingTid === t.id ? (
                            <>
                              <span style={{ fontSize: 11, color: theme.color.danger }}>Delete?</span>
                              <button onClick={() => handleDeleteTreatment(t.id)} style={{ fontSize: 11, color: theme.color.danger, background: "none", border: "none", cursor: "pointer", padding: 0, fontWeight: 600 }}>Yes</button>
                              <button onClick={() => setDeletingTid(null)} style={{ fontSize: 11, color: theme.color.muted, background: "none", border: "none", cursor: "pointer", padding: 0 }}>No</button>
                            </>
                          ) : (
                            <>
                              <button onClick={() => setEditingTid(t.id)} style={{ fontSize: 11, color: theme.color.accent, background: "none", border: "none", cursor: "pointer", padding: 0 }}>Edit</button>
                              <button onClick={() => setDeletingTid(t.id)} style={{ fontSize: 11, color: theme.color.danger, background: "none", border: "none", cursor: "pointer", padding: 0 }}>Delete</button>
                            </>
                          )}
                        </div>
                      </div>
                      <p style={{ margin: "0 0 4px", fontSize: 13, color: theme.color.text, lineHeight: 1.5 }}>{t.description}</p>
                      {(t.owner || t.dueDate || t.type === "accept") && (
                        <div style={{ fontSize: 11, color: theme.color.subtle }}>
                          {t.owner && <span>Owner: {t.owner}</span>}
                          {t.owner && t.dueDate && " · "}
                          {t.dueDate && <span>Due: {fmtDateOnly(t.dueDate)}</span>}
                          {t.type === "accept" && <span>{t.owner || t.dueDate ? " · " : ""}{acceptanceSummary(t)}</span>}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}

              {addingTreatment ? (
                <TreatmentForm onSave={handleAddTreatment} onCancel={() => setAddingTreatment(false)} appetite={appetite?.assessments[localRisk.id]} riskOwner={localRisk.owner} />
              ) : (
                <button onClick={() => setAddingTreatment(true)} style={{
                  fontSize: 13, padding: "8px 14px", border: `1px dashed ${theme.color.border}`,
                  borderRadius: 3, background: "transparent", cursor: "pointer", color: theme.color.accent, width: "100%", marginTop: 4,
                }}>
                  + Add treatment
                </button>
              )}
            </div>
          )}

          {/* Signals tab */}
          {!isNew && tab === "signals" && localRisk && (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>

              {crossRefsLoading && (
                <div style={{ fontSize: 12, color: theme.color.subtle, display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ width: 12, height: 12, border: `2px solid ${theme.color.accent}`, borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.8s linear infinite", display: "inline-block" }} />
                  Loading signals…
                </div>
              )}

              {crossRefs && <RiskLinksPanel riskId={localRisk.id} crossRefs={crossRefs} canEdit={canEdit} onChanged={reloadCrossRefs} />}

              {crossRefs && moduleLinkList(crossRefs).length > 0 && (
                <div style={{ background: theme.color.panelAlt, borderRadius: 3, padding: "12px 16px" }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: theme.color.heading, marginBottom: 6 }}>Across the platform ({moduleLinkList(crossRefs).length})</div>
                  <div style={{ ...text.hint, marginBottom: 8 }}>The incidents, actions, vulnerabilities, advisories, DPIAs, AI systems and exposure scenarios that name this risk. Link them from their own pages.</div>
                  {moduleLinkList(crossRefs).map((l, i) => (
                    <div key={`${l.kindLabel}-${i}`} style={{ display: "flex", gap: 8, fontSize: 11, marginBottom: 4 }}>
                      <span style={{ color: theme.color.subtle, minWidth: 110 }}>{l.kindLabel}</span>
                      {l.href ? <PortalLink to={l.href} style={{ flex: 1, color: theme.color.accentText }}>{l.label}</PortalLink> : <span style={{ flex: 1 }}>{l.label}</span>}
                    </div>
                  ))}
                </div>
              )}

              {crossRefs?.quantification && <QuantificationSummary q={crossRefs.quantification} />}

              {/* Treatment effectiveness */}
              {localRisk.treatments.length > 0 && (() => {
                const done  = localRisk.treatments.filter(t => t.status === "completed").length;
                const total = localRisk.treatments.length;
                const pct   = Math.round((done / total) * 100);
                const aboveTarget = localRisk.targetScore != null && localRisk.residualScore != null && localRisk.residualScore > localRisk.targetScore;
                return (
                  <div style={{ background: theme.color.panelAlt, borderRadius: 3, padding: "12px 16px" }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: theme.color.heading, marginBottom: 8 }}>Treatment effectiveness</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                      <RulerProgress value={pct} fill={pct === 100 ? theme.color.success : undefined} label="Treatments completed" style={{ flex: 1 }} />
                      <span style={{ fontSize: 11, fontWeight: 600, color: theme.color.muted, whiteSpace: "nowrap" }}>{done}/{total} completed ({pct}%)</span>
                    </div>
                    {localRisk.residualScore != null && (
                      <div style={{ fontSize: 11, color: theme.color.subtle }}>
                        Residual score: <ScoreBadge score={localRisk.residualScore} size="sm" />
                        {localRisk.targetScore != null && (
                          <span style={{ marginLeft: 8, color: aboveTarget ? theme.color.dangerText : theme.color.successText, fontWeight: 600 }}>
                            {aboveTarget ? `${localRisk.residualScore - localRisk.targetScore} above target (${localRisk.targetScore})` : `At or below target (${localRisk.targetScore})`}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Materialized risk — data breaches or DORA incidents linked */}
              {crossRefs && (crossRefs.dataBreaches.length > 0 || crossRefs.doraIncidents.length > 0 || (crossRefs.securityIncidents?.length ?? 0) > 0) && (
                <div style={{ background: theme.color.dangerBg, border: `1px solid ${theme.color.border}`, borderRadius: 3, padding: "12px 16px" }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: theme.color.danger, marginBottom: 6 }}>
                    <AlertTriangle size={13} style={{ verticalAlign: "-2px", marginRight: 4 }} />Risk has materialised
                  </div>
                  <div style={{ fontSize: 12, color: theme.color.muted, marginBottom: 8 }}>
                    This risk is linked to real-world incidents. Consider re-evaluating the inherent likelihood score upward.
                  </div>
                  {(crossRefs.securityIncidents?.length ?? 0) > 0 && (
                    <div style={{ fontSize: 11, color: theme.color.subtle, marginBottom: 4 }}>
                      <strong>Security incidents:</strong>{" "}
                      {(crossRefs.securityIncidents ?? []).map(i => `${padRef("INC", i.displayId)} ${i.title} (${i.status})`).join(", ")}
                    </div>
                  )}
                  {crossRefs.dataBreaches.length > 0 && (
                    <div style={{ fontSize: 11, color: theme.color.subtle, marginBottom: 4 }}>
                      <strong>Data breaches:</strong>{" "}
                      {crossRefs.dataBreaches.map(b => `${b.title} (${b.status})`).join(", ")}
                    </div>
                  )}
                  {crossRefs.doraIncidents.length > 0 && (
                    <div style={{ fontSize: 11, color: theme.color.subtle }}>
                      <strong>ICT incidents:</strong>{" "}
                      {crossRefs.doraIncidents.map(d => `${d.title}${d.isMajor ? " [major]" : ""} (${d.status})`).join(", ")}
                    </div>
                  )}
                </div>
              )}

              {/* SOA / control warnings */}
              {crossRefs && crossRefs.soaWarnings.length > 0 && (
                <div style={{ background: theme.color.warningBg, border: `1px solid ${theme.color.border}`, borderRadius: 3, padding: "12px 16px" }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: theme.color.warningText, marginBottom: 6 }}>
                    Control implementation gaps
                  </div>
                  <div style={{ fontSize: 12, color: theme.color.muted, marginBottom: 8 }}>
                    {crossRefs.soaWarnings.length} linked control{crossRefs.soaWarnings.length !== 1 ? "s are" : " is"} not yet implemented — residual score may understate the actual risk.
                  </div>
                  {crossRefs.soaWarnings.map(w => (
                    <div key={w.controlId} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 4, fontSize: 11 }}>
                      <StatusTag tone={w.status === "GAP" ? "gap" : "none"}>
                        {w.status === "GAP" ? "GAP" : "Not started"}
                      </StatusTag>
                      <span style={{ color: theme.color.muted }}>{w.controlName}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Compliance check failures */}
              {crossRefs && crossRefs.complianceGaps.length > 0 && (
                <div style={{ background: theme.color.dangerBg, border: `1px solid ${theme.color.border}`, borderRadius: 3, padding: "12px 16px" }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: theme.color.danger, marginBottom: 6 }}>
                    Compliance check failures
                  </div>
                  <div style={{ fontSize: 12, color: theme.color.muted, marginBottom: 8 }}>
                    {crossRefs.complianceGaps.length} linked control{crossRefs.complianceGaps.length !== 1 ? "s" : ""} failed the last AI compliance check — residual score may be overstated.
                  </div>
                  {crossRefs.complianceGaps.map(g => (
                    <div key={g.controlId} style={{ marginBottom: 8 }}>
                      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 3, fontSize: 11 }}>
                        <StatusTag tone={g.status === "GAP" ? "gap" : "partial"}>
                          {g.status === "GAP" ? "GAP" : "Partial"}
                        </StatusTag>
                        <span style={{ color: theme.color.muted, fontWeight: 500 }}>{g.controlName}</span>
                      </div>
                      {g.gaps.length > 0 && (
                        <ul style={{ margin: "0 0 0 24px", padding: 0, fontSize: 11, color: theme.color.subtle }}>
                          {g.gaps.slice(0, 2).map((gap, i) => <li key={i}>{gap}</li>)}
                          {g.gaps.length > 2 && <li style={{ color: theme.color.faint }}>+{g.gaps.length - 2} more</li>}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Converging signals from external connectors */}
              {crossRefs && crossRefs.convergingSignals.length > 0 && (
                <div style={{ background: theme.color.accentBg, border: `1px solid ${theme.color.border}`, borderRadius: 3, padding: "12px 16px" }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: theme.color.accent, marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}>
                    <Sparkles size={13} />
                    Converging signals from {crossRefs.convergingSignals.length} source{crossRefs.convergingSignals.length !== 1 ? "s" : ""}
                  </div>
                  <div style={{ fontSize: 12, color: theme.color.muted, marginBottom: 8 }}>
                    Multiple security integrations are reporting active issues simultaneously — this risk may warrant elevated priority.
                  </div>
                  {crossRefs.convergingSignals.map(s => (
                    <div key={s.source} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 4, fontSize: 11 }}>
                      <span style={{ padding: "1px 6px", borderRadius: 4, fontWeight: 600, background: theme.color.accentBg, color: theme.color.accentText }}>
                        {s.count}
                      </span>
                      <span style={{ color: theme.color.muted }}><strong>{s.source}</strong> — {s.label}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Related risk clustering */}
              {crossRefs && crossRefs.relatedRisks.length > 0 && (
                <div style={{ background: theme.color.panelAlt, borderRadius: 3, padding: "12px 16px" }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: theme.color.heading, marginBottom: 6 }}>Related open risks</div>
                  <div style={{ fontSize: 11, color: theme.color.subtle, marginBottom: 10 }}>
                    Other open risks sharing the same category, asset, or control — consider whether treatments can be coordinated.
                  </div>
                  {crossRefs.relatedRisks.map(r => (
                    <div key={r.id} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, fontSize: 11 }}>
                      <span style={{ fontVariantNumeric: "tabular-nums", color: theme.color.subtle, minWidth: 68 }}>
                        {r.displayId ? `RISK-${String(r.displayId).padStart(3, "0")}` : "—"}
                      </span>
                      <span style={{ flex: 1, color: theme.color.text, fontWeight: 500 }}>{r.title}</span>
                      <ScoreBadge score={r.residualScore ?? r.inherentScore} size="sm" />
                      <StatusPill status={r.status} />
                    </div>
                  ))}
                </div>
              )}

              {/* Risk dependencies */}
              {crossRefs && ((crossRefs.dependencySourceOf?.length ?? 0) + (crossRefs.dependencyTargetOf?.length ?? 0)) > 0 && (
                <div style={{ background: theme.color.panelAlt, borderRadius: 3, padding: "12px 16px" }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: theme.color.heading, marginBottom: 8 }}>Risk dependencies</div>
                  {(crossRefs.dependencySourceOf ?? []).length > 0 && (
                    <div style={{ marginBottom: 8 }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: theme.color.muted, marginBottom: 4 }}>This risk amplifies / enables →</div>
                      {(crossRefs.dependencySourceOf ?? []).map(dep => (
                        <div key={dep.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11, marginBottom: 4 }}>
                          <span style={{ fontVariantNumeric: "tabular-nums", color: theme.color.subtle, minWidth: 68 }}>
                            {dep.targetRisk?.displayId ? `RISK-${String(dep.targetRisk.displayId).padStart(3, "0")}` : "—"}
                          </span>
                          <span style={{ flex: 1, color: theme.color.text }}>{dep.targetRisk?.title ?? dep.targetRiskId}</span>
                          <span style={{ fontSize: 10, padding: "1px 5px", borderRadius: 4, background: theme.color.consultantBg, color: theme.color.consultant, fontWeight: 600 }}>{dep.dependencyType}</span>
                          {dep.targetRisk?.residualScore != null && <ScoreBadge score={dep.targetRisk.residualScore} size="sm" />}
                        </div>
                      ))}
                    </div>
                  )}
                  {(crossRefs.dependencyTargetOf ?? []).length > 0 && (
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 600, color: theme.color.muted, marginBottom: 4 }}>← Amplified / enabled by</div>
                      {(crossRefs.dependencyTargetOf ?? []).map(dep => (
                        <div key={dep.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11, marginBottom: 4 }}>
                          <span style={{ fontVariantNumeric: "tabular-nums", color: theme.color.subtle, minWidth: 68 }}>
                            {dep.sourceRisk?.displayId ? `RISK-${String(dep.sourceRisk.displayId).padStart(3, "0")}` : "—"}
                          </span>
                          <span style={{ flex: 1, color: theme.color.text }}>{dep.sourceRisk?.title ?? dep.sourceRiskId}</span>
                          <span style={{ fontSize: 10, padding: "1px 5px", borderRadius: 4, background: theme.color.consultantBg, color: theme.color.consultant, fontWeight: 600 }}>{dep.dependencyType}</span>
                          {dep.sourceRisk?.residualScore != null && <ScoreBadge score={dep.sourceRisk.residualScore} size="sm" />}
                        </div>
                      ))}
                    </div>
                  )}
                  {crossRefs.heatScore != null && (
                    <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${theme.color.border}`, fontSize: 11, color: theme.color.muted }}>
                      Heat score: <strong style={{ color: crossRefs.heatScore > 20 ? theme.color.dangerText : crossRefs.heatScore > 12 ? theme.color.warningText : theme.color.successText }}>{crossRefs.heatScore}</strong>
                      <span style={{ color: theme.color.subtle, marginLeft: 6 }}>(residual + active signals × 2 + overdue treatments × 3)</span>
                    </div>
                  )}
                </div>
              )}

              {/* Auditor findings surfaced here */}
              {crossRefs && crossRefs.auditorFindings.length > 0 && (
                <div style={{ background: theme.color.panelAlt, borderRadius: 3, padding: "12px 16px" }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: theme.color.heading, marginBottom: 6 }}>Linked auditor findings ({crossRefs.auditorFindings.length})</div>
                  {crossRefs.auditorFindings.map(f => (
                    <div key={f.id} style={{ display: "flex", gap: 8, alignItems: "flex-start", marginBottom: 6, fontSize: 11 }}>
                      <span style={{
                        padding: "1px 6px", borderRadius: 4, fontWeight: 600, whiteSpace: "nowrap", flexShrink: 0,
                        background: f.status === "open" ? theme.color.dangerBg : f.status === "acknowledged" ? theme.color.warningBg : theme.color.successBg,
                        color:      f.status === "open" ? theme.color.dangerText : f.status === "acknowledged" ? theme.color.warningText : theme.color.successText,
                      }}>
                        {f.status}
                      </span>
                      <span style={{ color: theme.color.muted }}>{f.text || f.controlId}</span>
                    </div>
                  ))}
                </div>
              )}

              {!crossRefsLoading && crossRefs && crossRefs.soaWarnings.length === 0 && crossRefs.complianceGaps.length === 0 && crossRefs.convergingSignals.length === 0 && crossRefs.dataBreaches.length === 0 && crossRefs.doraIncidents.length === 0 && crossRefs.relatedRisks.length === 0 && crossRefs.auditorFindings.length === 0 && (crossRefs.dependencySourceOf?.length ?? 0) === 0 && (crossRefs.dependencyTargetOf?.length ?? 0) === 0 && localRisk.treatments.length === 0 && (
                <p style={{ color: theme.color.subtle, fontSize: 13, textAlign: "center", padding: "20px 0" }}>
                  No signals or cross-references found for this risk.
                </p>
              )}
            </div>
          )}
        </div>

        {/* Footer: the view's Actions, or the form's save / cancel / delete */}
        {!isNew && !editing && canEdit && localRisk && (
          <div style={{ padding: "12px 24px 16px", borderTop: `1px solid ${theme.color.border}`, flexShrink: 0 }}>
            <RecordActions>
              {localRisk.status === "tentative_remediated" && (
                <>
                  <button onClick={() => setRiskFields({ status: "closed" })} style={{ ...btnPrimary, fontSize: 12, padding: "6px 12px" }}>Confirm remediation and close</button>
                  <button onClick={() => setRiskFields({ status: "open" })} style={actionBtn}>Revert to open</button>
                </>
              )}
              {localRisk.suggestedForRemoval && (
                <button onClick={() => setRiskFields({ suggestedForRemoval: false })} style={actionBtn}>Dismiss notice and keep risk</button>
              )}
              <button onClick={handleEvaluate} disabled={evalRunning} style={{ ...actionBtn, color: theme.color.accentText, borderColor: theme.color.accent, fontWeight: 600, opacity: evalRunning ? 0.6 : 1 }}>
                <Sparkles size={12} style={{ verticalAlign: "-1px", marginRight: 4 }} />{localRisk.evaluations?.[0] || evalStatus === "done" ? "Re-evaluate with AI" : "Evaluate with AI"}
              </button>
              <button onClick={() => { startEditing("treatments"); setAddingTreatment(true); }} style={actionBtn}>Add treatment</button>
              {evalProgress && <div style={{ flexBasis: "100%" }}>{evalProgress}</div>}
            </RecordActions>
          </div>
        )}
        {editing && !isNew && tab !== "details" && (
          <div style={{ padding: "16px 24px", borderTop: `1px solid ${theme.color.border}`, display: "flex", gap: 10, flexShrink: 0, alignItems: "center" }}>
            <button onClick={cancelEditing} style={{ fontSize: 13, padding: "9px 18px", border: `1px solid ${theme.color.border}`, borderRadius: 3, background: "transparent", cursor: "pointer", color: theme.color.muted }}>
              Back to the view
            </button>
            {tab === "treatments" && <span style={{ ...text.hint }}>Each treatment saves on its own.</span>}
            {writeError && <span role="alert" style={{ fontSize: 12, color: theme.color.dangerText, flex: 1 }}>{writeError}</span>}
          </div>
        )}
        {editing && tab === "details" && (
          <div style={{ padding: "16px 24px", borderTop: `1px solid ${theme.color.border}`, display: "flex", gap: 10, flexShrink: 0, alignItems: "center" }}>
            <button onClick={handleSave} disabled={saving || !title.trim() || !threat.trim()} style={{ ...btnPrimary, opacity: saving || !title.trim() || !threat.trim() ? 0.5 : 1 }}>
              {saving ? "Saving…" : isNew ? "Create risk" : "Save changes"}
            </button>
            <button onClick={cancelEditing} style={{ fontSize: 13, padding: "9px 18px", border: `1px solid ${theme.color.border}`, borderRadius: 3, background: "transparent", cursor: "pointer", color: theme.color.muted }}>
              Cancel
            </button>
            {writeError && <span role="alert" style={{ fontSize: 12, color: theme.color.dangerText, flex: 1, minWidth: 0 }}>{writeError}</span>}
            {!isNew && (
              <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
                {deletingRisk ? (
                  <>
                    <span style={{ fontSize: 12, color: theme.color.danger }}>Delete this risk?</span>
                    <button onClick={handleDeleteRisk} style={{ fontSize: 12, padding: "7px 14px", border: `1px solid ${theme.color.danger}`, borderRadius: 3, background: "transparent", cursor: "pointer", color: theme.color.danger, fontWeight: 600 }}>
                      Yes, delete
                    </button>
                    <button onClick={() => setDeletingRisk(false)} style={{ fontSize: 12, padding: "7px 14px", border: `1px solid ${theme.color.border}`, borderRadius: 3, background: "transparent", cursor: "pointer", color: theme.color.muted }}>
                      Cancel
                    </button>
                  </>
                ) : (
                  <button onClick={() => setDeletingRisk(true)} style={{ fontSize: 12, padding: "7px 14px", border: `1px solid ${theme.color.border}`, borderRadius: 3, background: "transparent", cursor: "pointer", color: theme.color.danger }}>
                    Delete risk
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ── AI suggestion types ───────────────────────────────────────────────────

interface SuggestedTreatment {
  type: Treatment["type"];
  description: string;
}

interface SuggestedRisk {
  title: string;
  category: string;
  threat: string;
  vulnerability?: string;
  assetOrProcess?: string;
  frameworkIds: string[];
  inherentLikelihood: number;
  inherentImpact: number;
  inherentScore: number;
  inherentLikelihoodRationale: string;
  inherentImpactRationale: string;
  residualLikelihood: number;
  residualImpact: number;
  residualScore: number;
  residualLikelihoodRationale: string;
  residualImpactRationale: string;
  suggestedTreatments: SuggestedTreatment[];
}

// ── Suggestion review panel ───────────────────────────────────────────────

function SuggestionPanel({
  suggestions, onAccept, onClose, onAllReviewed,
}: {
  suggestions: SuggestedRisk[];
  onAccept: (s: SuggestedRisk) => Promise<void>;
  onClose: () => void;
  onAllReviewed: () => void;
}) {
  const [dismissed, setDismissed] = useState<Set<number>>(new Set());
  const [accepted, setAccepted]   = useState<Set<number>>(new Set());
  const [accepting, setAccepting] = useState<number | null>(null);
  const [expanded, setExpanded]   = useState<number | null>(null);

  const remaining = suggestions.filter((_, i) => !dismissed.has(i) && !accepted.has(i));
  const doneCount = accepted.size + dismissed.size;

  async function handleAccept(s: SuggestedRisk, i: number) {
    setAccepting(i);
    try {
      await onAccept(s);
      setAccepted(prev => new Set([...prev, i]));
    } finally {
      setAccepting(null);
    }
  }

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 1000, display: "flex", alignItems: "flex-start", justifyContent: "flex-end" }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ ...floatingSheet, width: 600, maxWidth: "100vw", height: "100vh", display: "flex", flexDirection: "column" }}>
        <div style={{ padding: "20px 24px 16px", borderBottom: "1px solid var(--c-line)", flexShrink: 0 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div>
              <h2 style={{ margin: "0 0 4px", fontSize: 17, fontWeight: 700, color: "var(--c-heading)" }}>AI-suggested risks</h2>
              <p style={{ margin: 0, fontSize: 12, color: "var(--c-subtle)" }}>
                {remaining.length} remaining · {accepted.size} accepted · {dismissed.size} dismissed
              </p>
            </div>
            <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--c-muted)", fontSize: 20, padding: 0 }}>×</button>
          </div>
          {doneCount > 0 && (
            <RulerProgress value={doneCount} max={suggestions.length} label="Suggestions reviewed" height={10} style={{ marginTop: 10 }} />
          )}
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: "16px 24px" }}>
          {remaining.length === 0 && (
            <div style={{ textAlign: "center", padding: 40, fontSize: 13 }}>
              <div style={{ color: "var(--c-successText)", fontWeight: 600, marginBottom: 8 }}>All suggestions reviewed</div>
              <div style={{ color: "var(--c-subtle)", marginBottom: 20 }}>
                {accepted.size} accepted · {dismissed.size} dismissed
              </div>
              <button
                onClick={onAllReviewed}
                style={{ fontSize: 13, padding: "8px 20px", borderRadius: 3, border: "none", background: "var(--c-accent)", color: "#fff", cursor: "pointer", fontWeight: 600 }}
              >
                Done — run again
              </button>
            </div>
          )}
          {suggestions.map((s, i) => {
            if (dismissed.has(i) || accepted.has(i)) return null;
            const isExpanded = expanded === i;
            return (
              <div key={i} style={{ border: `1px solid var(--c-border)`, borderRadius: 3, marginBottom: 10, overflow: "hidden" }}>
                <div style={{ padding: "12px 14px", background: "var(--c-panelAlt)" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "var(--c-heading)", marginBottom: 4 }}>{s.title}</div>
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                        <span style={{ fontSize: 11, color: "var(--c-subtle)" }}>{s.category}</span>
                        <span style={{ fontSize: 11, color: "var(--c-subtle)" }}>·</span>
                        <span style={{ fontSize: 11, color: "var(--c-subtle)" }}>
                          Inherent: <strong style={{ color: scoreColor(s.inherentScore) }}>{s.inherentScore}</strong>
                          {" "}→ Residual: <strong style={{ color: scoreColor(s.residualScore) }}>{s.residualScore}</strong>
                        </span>
                        {s.frameworkIds.slice(0, 2).map(f => (
                          <span key={f} style={{ fontSize: 10, padding: "1px 5px", borderRadius: 4, background: "var(--c-accentBg)", color: "var(--c-accentText)" }}>
                            {FRAMEWORK_LABELS[f as FrameworkId] ?? f}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                      <button
                        onClick={() => handleAccept(s, i)}
                        disabled={accepting === i}
                        style={{ ...btnPrimary, fontSize: 12, padding: "5px 12px", opacity: accepting === i ? 0.6 : 1 }}
                      >
                        {accepting === i ? "Adding…" : "Accept"}
                      </button>
                      <button
                        onClick={() => setDismissed(prev => new Set([...prev, i]))}
                        style={{ fontSize: 12, padding: "5px 10px", border: `1px solid var(--c-border)`, borderRadius: 3, background: "transparent", cursor: "pointer", color: "var(--c-muted)" }}
                      >
                        Dismiss
                      </button>
                      <button
                        onClick={() => setExpanded(isExpanded ? null : i)}
                        style={{ fontSize: 11, padding: "5px 10px", border: `1px solid var(--c-border)`, borderRadius: 3, background: "transparent", cursor: "pointer", color: "var(--c-subtle)" }}
                      >
                        {isExpanded ? "▲" : "▼"}
                      </button>
                    </div>
                  </div>
                </div>

                {isExpanded && (
                  <div style={{ padding: "14px 14px 16px" }}>
                    <p style={{ margin: "0 0 10px", fontSize: 13, color: "var(--c-text)", lineHeight: 1.6 }}>{s.threat}</p>
                    {s.vulnerability && (
                      <p style={{ margin: "0 0 10px", fontSize: 12, color: "var(--c-subtle)" }}>
                        <strong>Vulnerability:</strong> {s.vulnerability}
                      </p>
                    )}
                    {s.assetOrProcess && (
                      <p style={{ margin: "0 0 14px", fontSize: 12, color: "var(--c-subtle)" }}>
                        <strong>Asset / process:</strong> {s.assetOrProcess}
                      </p>
                    )}
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
                      <div style={{ background: "var(--c-panelAlt)", borderRadius: 3, padding: 10 }}>
                        <div style={{ fontSize: 11, fontWeight: 600, color: "var(--c-heading)", marginBottom: 6 }}>
                          Inherent — <ScoreBadge score={s.inherentScore} size="sm" /> {scoreLabel(s.inherentScore)}
                        </div>
                        <p style={{ margin: "0 0 4px", fontSize: 11, color: "var(--c-subtle)" }}>
                          <strong>Likelihood ({s.inherentLikelihood}/5):</strong> {s.inherentLikelihoodRationale}
                        </p>
                        <p style={{ margin: 0, fontSize: 11, color: "var(--c-subtle)" }}>
                          <strong>Impact ({s.inherentImpact}/5):</strong> {s.inherentImpactRationale}
                        </p>
                      </div>
                      <div style={{ background: "var(--c-panelAlt)", borderRadius: 3, padding: 10 }}>
                        <div style={{ fontSize: 11, fontWeight: 600, color: "var(--c-heading)", marginBottom: 6 }}>
                          Residual — <ScoreBadge score={s.residualScore} size="sm" /> {scoreLabel(s.residualScore)}
                        </div>
                        <p style={{ margin: "0 0 4px", fontSize: 11, color: "var(--c-subtle)" }}>
                          <strong>Likelihood ({s.residualLikelihood}/5):</strong> {s.residualLikelihoodRationale}
                        </p>
                        <p style={{ margin: 0, fontSize: 11, color: "var(--c-subtle)" }}>
                          <strong>Impact ({s.residualImpact}/5):</strong> {s.residualImpactRationale}
                        </p>
                      </div>
                    </div>
                    {s.suggestedTreatments.length > 0 && (
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 600, color: "var(--c-heading)", marginBottom: 6 }}>
                          Suggested treatments ({s.suggestedTreatments.length})
                        </div>
                        {s.suggestedTreatments.map((t, ti) => (
                          <div key={ti} style={{ display: "flex", gap: 8, marginBottom: 5, fontSize: 12 }}>
                            <span style={{ fontSize: 10, padding: "2px 6px", borderRadius: 4, background: "var(--c-accentBg)", color: "var(--c-accentText)", fontWeight: 600, flexShrink: 0, alignSelf: "flex-start", marginTop: 1 }}>
                              {t.type}
                            </span>
                            <span style={{ color: "var(--c-text)", lineHeight: 1.5 }}>{t.description}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div style={{ padding: "14px 24px", borderTop: `1px solid var(--c-border)`, flexShrink: 0, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: 12, color: "var(--c-subtle)" }}>Accepted risks are added to your register immediately.</span>
          <button
            onClick={remaining.length === 0 ? onAllReviewed : onClose}
            style={{ fontSize: 13, padding: "7px 16px", border: `1px solid var(--c-border)`, borderRadius: 3, background: "transparent", cursor: "pointer", color: "var(--c-muted)" }}
          >
            {remaining.length === 0 ? "Done" : "Close"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Enrichment signals ────────────────────────────────────────────────────

type SignalSource =
  | "COMPLIANCE_GAP" | "INTUNE" | "THREAT_FEED"
  | "SENTINEL_INCIDENT" | "DEFENDER_ALERT"
  | "MFA_GAP" | "CA_POLICY_GAP" | "SECURE_SCORE_DROP"
  | "AWS_FINDING" | "GCP_FINDING" | "AUDITOR_FINDING"
  // From the platform's own registers (backend lib/riskModuleSignals.ts).
  | "SECURITY_INCIDENT" | "VULNERABILITY_SLA" | "HARDENING_FAILURE" | "ZONE_FLOW" | "EXERCISE_FINDING"
  | "DPIA_HIGH_RESIDUAL" | "AI_HIGH_RISK" | "DATA_DISCOVERY" | "SUPPLIER_CERT_EXPIRY" | "LEAVER_OVERDUE";

const MODULE_SIGNAL_SOURCES: SignalSource[] = [
  "SECURITY_INCIDENT", "VULNERABILITY_SLA", "HARDENING_FAILURE", "ZONE_FLOW", "EXERCISE_FINDING",
  "DPIA_HIGH_RESIDUAL", "AI_HIGH_RISK", "DATA_DISCOVERY", "SUPPLIER_CERT_EXPIRY", "LEAVER_OVERDUE",
];

interface EnrichmentSignal {
  id:          string;
  source:      SignalSource;
  title:       string;
  detail:      string;
  severity:    "critical" | "high" | "medium" | "low";
  createdAt:   string;
  riskId:      string | null;
  risk:        { id: string; title: string; displayId: number | null } | null;
}

interface CorrelatedGroup {
  category: string;
  sources:  string[];
  count:    number;
  severity: string;
  riskId:   string | null;
}

/** "COMPLIANCE_GAP" -> "Compliance gap", for a source the label table does not know (DOC-21). */
const sentenceCaseCode = (code: string) => { const w = code.toLowerCase().replace(/_/g, " "); return w.charAt(0).toUpperCase() + w.slice(1); };

const SIGNAL_SOURCE_META: Record<SignalSource, { label: string; color: string; bg: string }> = cbPalette({
  COMPLIANCE_GAP:    { label: "Control Gap",     color: "#b45309", bg: "#fef3c7" },
  INTUNE:            { label: "Intune",           color: "#0369a1", bg: "#e0f2fe" },
  THREAT_FEED:       { label: "Threat Intel",     color: "#b91c1c", bg: "#fee2e2" },
  SENTINEL_INCIDENT: { label: "Sentinel",         color: "#7c3aed", bg: "#ede9fe" },
  DEFENDER_ALERT:    { label: "Defender",         color: "#0e7490", bg: "#cffafe" },
  MFA_GAP:           { label: "MFA Gap",          color: "#dc2626", bg: "#fee2e2" },
  CA_POLICY_GAP:     { label: "CA Policy",        color: "#c2410c", bg: "#ffedd5" },
  SECURE_SCORE_DROP: { label: "Secure Score",     color: "#b45309", bg: "#fef3c7" },
  AWS_FINDING:       { label: "AWS Security Hub", color: "#92400e", bg: "#fef3c7" },
  GCP_FINDING:       { label: "GCP SCC",          color: "#166534", bg: "#dcfce7" },
  AUDITOR_FINDING:   { label: "Auditor Finding",  color: "#1e40af", bg: "#dbeafe" },
  SECURITY_INCIDENT:    { label: "Incident",          color: theme.color.dangerText,  bg: theme.color.dangerBg },
  VULNERABILITY_SLA:    { label: "Vulnerability",     color: theme.color.dangerText,  bg: theme.color.dangerBg },
  HARDENING_FAILURE:    { label: "CIS hardening",     color: theme.color.warningText, bg: theme.color.warningBg },
  ZONE_FLOW:            { label: "Network zones",     color: theme.color.warningText, bg: theme.color.warningBg },
  EXERCISE_FINDING:     { label: "Exercise",          color: theme.color.accentText,  bg: theme.color.accentBg },
  DPIA_HIGH_RESIDUAL:   { label: "DPIA",              color: theme.color.accentText,  bg: theme.color.accentBg },
  AI_HIGH_RISK:         { label: "AI system",         color: theme.color.accentText,  bg: theme.color.accentBg },
  DATA_DISCOVERY:       { label: "Data discovery",    color: theme.color.warningText, bg: theme.color.warningBg },
  SUPPLIER_CERT_EXPIRY: { label: "Supplier",          color: theme.color.consultant,  bg: theme.color.consultantBg },
  LEAVER_OVERDUE:       { label: "Leaver",            color: theme.color.warningText, bg: theme.color.warningBg },
});

const SIGNAL_SEV_COLOR: Record<string, string> = cbPalette({
  critical: "#b91c1c", high: "#c2410c", medium: "#b45309", low: "#15803d",
});

function SignalsPanel({ onAddRisk, canEdit }: { onAddRisk: () => void; canEdit: boolean }) {
  const [signals,    setSignals]    = useState<EnrichmentSignal[]>([]);
  const [correlated, setCorrelated] = useState<CorrelatedGroup[]>([]);
  const [expanded,   setExpanded]   = useState(false);
  const [loading,    setLoading]    = useState(true);

  useEffect(() => {
    api.get<{ signals: EnrichmentSignal[]; correlatedGroups?: CorrelatedGroup[] }>("/risks/enrichment-signals")
      .then(d => { setSignals(d.signals); setCorrelated(d.correlatedGroups ?? []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  async function dismiss(id: string) {
    await api.patch(`/risks/enrichment-signals/${id}`, { status: "DISMISSED" }).catch(() => {});
    setSignals(prev => prev.filter(s => s.id !== id));
  }

  async function apply(id: string) {
    await api.patch(`/risks/enrichment-signals/${id}`, { status: "APPLIED" }).catch(() => {});
    setSignals(prev => prev.filter(s => s.id !== id));
    onAddRisk();
  }

  if (loading || (signals.length === 0 && correlated.length === 0)) return null;

  const criticalCount = signals.filter(s => s.severity === "critical").length;
  const highCount     = signals.filter(s => s.severity === "high").length;
  const chipColor     = criticalCount > 0 ? theme.color.dangerText : highCount > 0 ? "#c2410c" : theme.color.warningText;
  const chipBg        = criticalCount > 0 ? theme.color.dangerBg   : highCount > 0 ? "#ffedd5"  : theme.color.warningBg;

  return (
    <div style={{ ...card, padding: 0, marginBottom: 14, borderColor: chipColor, overflow: "hidden" }}>
      {/* Header */}
      <button
        onClick={() => setExpanded(e => !e)}
        style={{
          width: "100%", display: "flex", alignItems: "center", gap: 10,
          padding: "11px 16px", background: "transparent", border: "none",
          cursor: "pointer", textAlign: "left",
        }}
      >
        <Zap size={14} color={chipColor} />
        <span style={{ fontSize: 13, fontWeight: 600, color: chipColor, flex: 1 }}>
          {signals.length} enrichment signal{signals.length > 1 ? "s" : ""}
          {correlated.length > 0 ? ` · ${correlated.length} converging group${correlated.length > 1 ? "s" : ""}` : ""}
          {" "}— data-driven risk nudges
        </span>
        <span style={{
          fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 4,
          background: chipBg, color: chipColor,
        }}>
          {criticalCount > 0 ? `${criticalCount} critical` : highCount > 0 ? `${highCount} high` : `${signals.length} pending`}
        </span>
        {expanded ? <ChevronUp size={14} color={theme.color.muted} /> : <ChevronDown size={14} color={theme.color.muted} />}
      </button>

      {expanded && (
        <div style={{ borderTop: `1px solid ${theme.color.border}` }}>
          {/* Correlated signal groups — multi-source convergence */}
          {correlated.length > 0 && (
            <div style={{ padding: "10px 16px", background: "#f5f3ff", borderBottom: `1px solid ${theme.color.border}` }}>
              <p style={{ margin: "0 0 6px", fontSize: 11, fontWeight: 700, color: "#7c3aed", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                Converging signals ({correlated.length} group{correlated.length > 1 ? "s" : ""})
              </p>
              {correlated.map((grp, gi) => (
                <div key={gi} style={{ fontSize: 12, color: theme.color.text, marginBottom: gi < correlated.length - 1 ? 4 : 0 }}>
                  <span style={{ fontWeight: 600, color: SIGNAL_SEV_COLOR[grp.severity] ?? theme.color.muted }}>
                    {grp.severity.toUpperCase()}
                  </span>
                  {" — "}{grp.count} signal{grp.count > 1 ? "s" : ""} from {grp.sources.map((src) => (SIGNAL_SOURCE_META as Record<string, { label: string } | undefined>)[src]?.label ?? sentenceCaseCode(src)).join(", ")}
                  {grp.riskId && <span style={{ color: theme.color.muted }}> → linked to a risk</span>}
                </div>
              ))}
            </div>
          )}
          {signals.map((sig, i) => {
            const sm = SIGNAL_SOURCE_META[sig.source] ?? { label: sentenceCaseCode(sig.source), color: theme.color.muted, bg: theme.color.border };
            const isActionable = ["COMPLIANCE_GAP", "SENTINEL_INCIDENT", "DEFENDER_ALERT", "MFA_GAP", "CA_POLICY_GAP", "AWS_FINDING", "GCP_FINDING", "AUDITOR_FINDING"].includes(sig.source)
              // A register signal that already names its risk asks for a reassessment, not a new risk.
              || (MODULE_SIGNAL_SOURCES.includes(sig.source) && !sig.riskId);
            return (
              <div key={sig.id} style={{
                padding: "12px 16px",
                borderBottom: i < signals.length - 1 ? `1px solid ${theme.color.border}` : undefined,
                display: "flex", gap: 12, alignItems: "flex-start",
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 5, alignItems: "center" }}>
                    <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 4, background: sm.bg, color: sm.color, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                      {sm.label}
                    </span>
                    <span style={{ fontSize: 10, fontWeight: 700, color: SIGNAL_SEV_COLOR[sig.severity] ?? theme.color.muted, textTransform: "uppercase" }}>
                      {sig.severity}
                    </span>
                    {sig.risk && (
                      <span style={{ fontSize: 11, color: theme.color.muted }}>
                        → {sig.risk.displayId ? `RISK-${String(sig.risk.displayId).padStart(3, "0")} ` : ""}{sig.risk.title}
                      </span>
                    )}
                  </div>
                  <p style={{ margin: "0 0 4px", fontSize: 13, fontWeight: 600, color: theme.color.heading }}>{sig.title}</p>
                  <p style={{ margin: 0, fontSize: 12, color: theme.color.muted, lineHeight: 1.5, whiteSpace: "pre-line" }}>{sig.detail}</p>
                </div>
                <div style={{ display: "flex", gap: 6, flexShrink: 0, alignItems: "flex-start", paddingTop: 2 }}>
                  {canEdit && isActionable && (
                    <button
                      onClick={() => apply(sig.id)}
                      style={{ fontSize: 11, padding: "4px 10px", borderRadius: 3, border: `1px solid ${theme.color.accent}`, background: theme.color.accentBg, color: theme.color.accentText, cursor: "pointer", fontWeight: 600, whiteSpace: "nowrap" }}
                    >
                      Add risk
                    </button>
                  )}
                  {canEdit && <button
                    onClick={() => dismiss(sig.id)}
                    title="Dismiss"
                    style={{ padding: "4px 6px", borderRadius: 3, border: `1px solid ${theme.color.border}`, background: "transparent", color: theme.color.muted, cursor: "pointer", display: "flex", alignItems: "center" }}
                  >
                    <X size={12} />
                  </button>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────

export function RiskRegisterPage() {
  const { frameworks } = useFrameworks();
  const [risks, setRisks]         = useState<Risk[]>([]);
  const [loading, setLoading]     = useState(true);
  const [view, setView]           = useState<"list" | "matrix" | "trend">("list");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterFw, setFilterFw]   = useState("");
  // ?site=<id> (from a site's detail page) opens the register filtered to it;
  // "none" keeps organisation-wide risks only.
  const [searchParams, setSearchParams] = useSearchParams();
  const [filterSite, setFilterSite] = useState(() => searchParams.get("site") ?? "");
  // ?asset=<id> (from the asset register's Links column) narrows to the risks
  // raised against that asset. Cleared from the banner, not from a dropdown:
  // it is a link through from somewhere else, not a filter of this page.
  const filterAsset = searchParams.get("asset") ?? "";
  const [sites, setSites]         = useState<Array<{ id: string; name: string; country: string }>>([]);
  const [search, setSearch]       = useState("");
  const tagData                   = useEntityTagData();
  const [tagFilter, setTagFilter] = useTagFilter();
  // The risk open in its view (or "new" for the add form). ?risk=<id> opens one from anywhere.
  const [editingRisk, setEditingRisk] = useState<Risk | null | "new">(null);
  const { user } = useCurrentUser();
  // Every write under /risks needs risks:write (requireWritePermission on the router).
  const canEdit = !!user?.permissions?.includes("risks:write");
  const riskParam = searchParams.get("risk");
  // Risk appetite: the thresholds and every risk's verdict (GET /risks/appetite). Setting the
  // appetite needs org:admin on the server; the drawer turns read-only on a 403 either way.
  const [appetite, setAppetite]               = useState<RiskAppetiteView | null>(null);
  const [showAppetite, setShowAppetite]       = useState(false);
  const [appetiteVersion, setAppetiteVersion] = useState(0);
  const canSetAppetite = !!user?.permissions?.includes("org:admin");

  function openRisk(r: Risk) {
    setEditingRisk(r);
    if (searchParams.get("risk") !== r.id) {
      const next = new URLSearchParams(searchParams); next.set("risk", r.id); setSearchParams(next, { replace: true });
    }
  }
  function closeRisk() {
    setEditingRisk(null);
    if (searchParams.has("risk")) {
      const next = new URLSearchParams(searchParams); next.delete("risk"); setSearchParams(next, { replace: true });
    }
  }

  const [suggestRunId, setSuggestRunId]   = useState<string | null>(null);
  const [suggestStatus, setSuggestStatus] = useState<"idle" | "queued" | "running" | "done" | "empty" | "error">("idle");
  const [suggestions, setSuggestions]     = useState<SuggestedRisk[]>([]);
  const [showPanel, setShowPanel]         = useState(false);
  const [suggestError, setSuggestError]   = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollFailuresRef = useRef(0);
  const pollStartRef = useRef<number>(0);

  useEffect(() => {
    if (!suggestRunId || suggestStatus === "done" || suggestStatus === "error" || suggestStatus === "idle") return;
    if (pollRef.current) clearInterval(pollRef.current);
    pollFailuresRef.current = 0;
    pollStartRef.current = Date.now();
    pollRef.current = setInterval(async () => {
      if (Date.now() - pollStartRef.current > 5 * 60 * 1000) {
        clearInterval(pollRef.current!);
        pollRef.current = null;
        setSuggestStatus("error");
        setSuggestError("AI analysis timed out — the worker may be busy. Please try again.");
        return;
      }
      try {
        const data = await api.get<{ status: string; suggestions: SuggestedRisk[] | null; errorMessage: string | null; }>(`/risks/suggestions/${suggestRunId}`);
        pollFailuresRef.current = 0;
        setSuggestStatus(data.status as typeof suggestStatus);
        if (data.status === "done" && data.suggestions) {
          clearInterval(pollRef.current!);
          pollRef.current = null;
          if (data.suggestions.length > 0) {
            setSuggestions(data.suggestions);
            setSuggestStatus("done");
            setShowPanel(true);
          } else {
            setSuggestStatus("empty");
          }
        } else if (data.status === "error") {
          setSuggestError(data.errorMessage ?? "AI suggestion failed");
          clearInterval(pollRef.current!);
          pollRef.current = null;
        }
      } catch {
        pollFailuresRef.current += 1;
        if (pollFailuresRef.current >= 5) {
          clearInterval(pollRef.current!);
          pollRef.current = null;
          setSuggestStatus("error");
          setSuggestError("Lost connection while waiting for AI results. Please try again.");
        }
      }
    }, 3000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [suggestRunId, suggestStatus]);

  useEffect(() => {
    api.get<{ runId: string | null; status: string; suggestions: SuggestedRisk[] | null }>("/risks/suggestions/latest")
      .then(data => {
        if (!data.runId) return;
        if (data.status === "done" && data.suggestions && data.suggestions.length > 0) {
          setSuggestions(data.suggestions);
          setSuggestRunId(data.runId);
          setSuggestStatus("done");
        } else if (data.status === "queued" || data.status === "running") {
          setSuggestRunId(data.runId);
          setSuggestStatus(data.status as "queued" | "running");
        }
      })
      .catch(() => {});
  }, []);

  async function handleSuggest() {
    setSuggestStatus("queued");
    setSuggestError(null);
    try {
      const { runId } = await api.post<{ runId: string }>("/risks/suggest");
      setSuggestRunId(runId);
    } catch {
      setSuggestStatus("error");
      setSuggestError("Failed to start AI suggestion");
    }
  }

  function handleAllReviewed() {
    setShowPanel(false);
    setSuggestions([]);
    setSuggestRunId(null);
    setSuggestStatus("idle");
  }

  async function handleAcceptSuggestion(s: SuggestedRisk) {
    const result = await api.post<{ risk: Risk }>("/risks", {
      title:             s.title,
      category:          s.category,
      threat:            s.threat,
      vulnerability:     s.vulnerability,
      assetOrProcess:    s.assetOrProcess,
      frameworkIds:      s.frameworkIds,
      status:            "open",
      inherentLikelihood: s.inherentLikelihood,
      inherentImpact:    s.inherentImpact,
      residualLikelihood: s.residualLikelihood,
      residualImpact:    s.residualImpact,
      inherentOverridden: false,
      residualOverridden: false,
    });
    const newRisk = result.risk;
    for (const t of s.suggestedTreatments) {
      try {
        await api.post(`/risks/${newRisk.id}/treatments`, { type: t.type, description: t.description, status: "planned" });
      } catch { /* non-fatal */ }
    }
    const fresh = await api.get<{ risk: Risk }>(`/risks/${newRisk.id}`);
    setRisks(prev => [fresh.risk, ...prev]);
  }

  useEffect(() => {
    api.get<{ risks: Risk[] }>("/risks", {
      ...(filterStatus ? { status: filterStatus } : {}),
      ...(filterFw     ? { framework: filterFw }  : {}),
      ...(filterSite   ? { siteId: filterSite }   : {}),
      ...(filterAsset  ? { assetId: filterAsset } : {}),
    })
      .then(d => setRisks(d.risks))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [filterStatus, filterFw, filterSite, filterAsset]);

  useEffect(() => {
    api.get<{ sites: Array<{ id: string; name: string; country: string }> }>("/sites")
      .then(d => setSites(d.sites)).catch(() => {});
  }, []);

  // The verdicts follow the risks: re-read whenever the list or the appetite itself changes, so a
  // new score, treatment or acceptance shows its verdict without a reload.
  useEffect(() => {
    let live = true;
    loadRiskAppetite().then(v => { if (live) setAppetite(v); }).catch(() => {});
    return () => { live = false; };
  }, [risks, appetiteVersion]);

  // ?risk=<id>: open that risk's view, from the list when it is loaded, else on its own
  // (it may sit outside the current filters).
  useEffect(() => {
    if (!riskParam || loading) return;
    if (editingRisk && editingRisk !== "new" && editingRisk.id === riskParam) return;
    const listed = risks.find(r => r.id === riskParam);
    if (listed) { setEditingRisk(listed); return; }
    let live = true;
    api.get<{ risk: Risk }>(`/risks/${encodeURIComponent(riskParam)}`)
      .then(d => { if (live) setEditingRisk(d.risk); })
      .catch(() => {});
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [riskParam, loading]);

  function handleSaved(r: Risk) {
    if (r.status === "__deleted__") {
      setRisks(prev => prev.filter(p => p.id !== r.id));
      closeRisk();
      return;
    }
    setRisks(prev => {
      const idx = prev.findIndex(p => p.id === r.id);
      return idx >= 0 ? prev.map(p => p.id === r.id ? r : p) : [r, ...prev];
    });
    // A new risk opens in its view once created.
    if (editingRisk === "new") openRisk(r);
  }

  const searchLower = search.toLowerCase();
  const visibleRisks = risks.filter(r =>
    matchesEntityTagFilter(tagData, entityRef({ kind: "risk", id: r.id }), tagFilter) &&
    (!searchLower ||
      r.title.toLowerCase().includes(searchLower) ||
      r.category.toLowerCase().includes(searchLower) ||
      (r.owner ?? "").toLowerCase().includes(searchLower) ||
      (riskId(r)).toLowerCase().includes(searchLower))
  );

  const openCount               = risks.filter(r => r.status === "open").length;
  const underTreatmentCount     = risks.filter(r => r.status === "under_treatment").length;
  const tentativeCount          = risks.filter(r => r.status === "tentative_remediated").length;
  const pendingRemovalCount     = risks.filter(r => r.suggestedForRemoval).length;
  const criticalCount           = risks.filter(r => (r.residualScore ?? r.inherentScore ?? 0) > 12).length;
  const overdueCount            = risks.filter(r => isOverdueReview(r) || hasOverdueTreatment(r)).length;
  const aboveAppetiteCount      = appetite ? risks.filter(r => r.status !== "closed" && appetite.assessments[r.id]?.verdict === "above").length : 0;

  return (
    <div className="page-pad">
      {/* Page header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: "0 0 4px", fontSize: 20, fontWeight: 700, color: theme.color.heading, letterSpacing: "-0.3px" }}>Risk register</h1>
          <p style={{ margin: 0, fontSize: 13, color: theme.color.subtle }}>
            Identify, assess, and treat information security risks across all enrolled frameworks.
          </p>
          <PlaybookBar ids={["risk-management"]} />
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {/* Suggesting and adding both write risks: offered only with risks:write, as the API requires (AUD-44). */}
          {!canEdit ? null : suggestStatus === "idle" || suggestStatus === "error" ? (
            <button
              onClick={handleSuggest}
              style={{ fontSize: 13, padding: "9px 16px", border: `1px solid var(--c-border)`, borderRadius: 3, background: "transparent", cursor: "pointer", color: "var(--c-accent)", fontWeight: 600, whiteSpace: "nowrap" }}
            >
              <Sparkles size={13} style={{ verticalAlign: "middle", marginRight: 5 }} />Suggest with AI
            </button>
          ) : suggestStatus === "done" ? (
            <button
              onClick={() => setShowPanel(true)}
              style={{ fontSize: 13, padding: "9px 16px", border: `1px solid var(--c-accent)`, borderRadius: 3, background: "var(--c-accentBg)", cursor: "pointer", color: "var(--c-accentText)", fontWeight: 600, whiteSpace: "nowrap" }}
            >
              <Sparkles size={13} style={{ verticalAlign: "middle", marginRight: 5 }} />View suggestions ({suggestions.length})
            </button>
          ) : suggestStatus === "empty" ? (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4, maxWidth: 320 }}>
              <span style={{ fontSize: 11, color: "var(--c-subtle)", textAlign: "right", lineHeight: 1.4 }}>
                AI risk assessment has identified all it can. Additional risks may be present and require manual addition and assessment.
              </span>
              <button
                onClick={() => setSuggestStatus("idle")}
                style={{ fontSize: 11, padding: "4px 10px", border: `1px solid var(--c-border)`, borderRadius: 3, background: "transparent", cursor: "pointer", color: "var(--c-muted)", whiteSpace: "nowrap" }}
              >
                Run again
              </button>
            </div>
          ) : (
            <span style={{ fontSize: 12, color: "var(--c-subtle)", padding: "9px 4px", display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ width: 12, height: 12, border: `2px solid var(--c-accent)`, borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.8s linear infinite", display: "inline-block", flexShrink: 0 }} />
              {suggestStatus === "queued" ? "Queuing…" : "AI analysing…"}
            </span>
          )}
          {suggestError && (
            <span style={{ fontSize: 11, color: "var(--c-danger)", maxWidth: 240 }} title={suggestError}>
              <AlertTriangle size={11} style={{ verticalAlign: "-1px", marginRight: 3 }} />{suggestError.length > 60 ? suggestError.slice(0, 57) + "…" : suggestError}
            </span>
          )}
          <button
            type="button"
            onClick={() => setShowAppetite(true)}
            disabled={!appetite}
            title={aboveAppetiteCount > 0 ? `${aboveAppetiteCount} risk${aboveAppetiteCount === 1 ? "" : "s"} above appetite` : "The thresholds each risk is compared with"}
            style={{ fontSize: 13, padding: "9px 16px", border: `1px solid var(--c-border)`, borderRadius: 3, background: "transparent", cursor: appetite ? "pointer" : "default", color: "var(--c-text)", fontWeight: 600, whiteSpace: "nowrap", display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            Risk appetite
            {aboveAppetiteCount > 0 && <span style={countTag}>{aboveAppetiteCount}</span>}
          </button>
          {canEdit && (
            <button onClick={() => setEditingRisk("new")} style={{ ...btnPrimary, whiteSpace: "nowrap" }}>
              + Add risk
            </button>
          )}
        </div>
      </div>

      {/* Enrichment signals */}
      <SignalsPanel onAddRisk={() => setEditingRisk("new")} canEdit={canEdit} />

      {/* KPI strip — 4 status tiles */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 12 }}>
        {[
          { label: "Total risks", value: risks.length.toString(), sub: filterStatus || filterFw || filterSite || filterAsset ? "matching the filter" : "in register", color: undefined },
          { label: "Open / unaddressed", value: openCount.toString(), sub: "need treatment", color: openCount > 0 ? theme.color.warning : theme.color.success },
          { label: "Pending client review", value: (tentativeCount + pendingRemovalCount).toString(), sub: `${tentativeCount} tentatively remediated · ${pendingRemovalCount} flagged for removal`, color: (tentativeCount + pendingRemovalCount) > 0 ? theme.color.auditor : undefined },
          { label: "Overdue", value: overdueCount.toString(), sub: "review or treatment past due", color: overdueCount > 0 ? theme.color.danger : theme.color.success, icon: overdueCount > 0 },
        ].map(k => (
          <div key={k.label} style={{ ...card, padding: 16 }}>
            <div style={{ fontSize: 11, color: theme.color.subtle, marginBottom: 4 }}>{k.label}</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: k.color ?? theme.color.heading, display: "flex", alignItems: "center", gap: 6 }}>
              {k.value}
              {k.icon && <AlertTriangle size={16} color={theme.color.danger} />}
            </div>
            <div style={{ fontSize: 11, color: theme.color.subtle }}>{k.sub}</div>
          </div>
        ))}
      </div>

      {/* Severity breakdown strip */}
      {risks.length > 0 && (() => {
        const counts = countBySeverity(risks);
        return (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 24 }}>
            {SEVERITY_ORDER.map(sev => {
              const meta = SEVERITY_META[sev];
              const count = counts[sev];
              return (
                <div key={sev} style={{ ...card, padding: "12px 16px", borderLeft: `3px solid ${meta.text}`, display: "flex", alignItems: "center", gap: 14 }}>
                  <div style={{ fontSize: 28, fontWeight: 800, color: meta.text, lineHeight: 1, fontVariantNumeric: "tabular-nums", minWidth: 32 }}>
                    {count}
                  </div>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: meta.text }}>{meta.label}</div>
                    <div style={{ fontSize: 10, color: theme.color.subtle }}>score {meta.range}</div>
                  </div>
                </div>
              );
            })}
          </div>
        );
      })()}

      {/* Filters + search + view toggle */}
      <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 16, flexWrap: "wrap" }}>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search risks…"
          style={{ ...inputStyle, width: 180 }}
        />
        {/* A risk's office is its siteId column, filtered by the select below (with its "Organisation-wide
            only" option), so the tag filter leaves the Office dimension out: one filter, not two (AUD-43). */}
        <EntityTagFilters value={tagFilter} onChange={setTagFilter} dimensions={["department", "jurisdiction", "type"]} />
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ ...inputStyle, width: "auto" }}>
          <option value="">All statuses</option>
          {STATUSES.map(s => <option key={s} value={s}>{statusLabel(s)}</option>)}
        </select>
        <select value={filterFw} onChange={e => setFilterFw(e.target.value)} style={{ ...inputStyle, width: "auto" }}>
          <option value="">All frameworks</option>
          {frameworks.map(f => <option key={f.id} value={f.frameworkId}>{FRAMEWORK_LABELS[f.frameworkId] ?? f.frameworkId}</option>)}
        </select>
        {sites.length > 0 && (
          <select value={filterSite} onChange={e => setFilterSite(e.target.value)} title="Risks raised at one site" style={{ ...inputStyle, width: "auto", ...(filterSite ? { borderColor: theme.color.accent, color: theme.color.accentText } : {}) }}>
            <option value="">All offices</option>
            {sites.map(x => <option key={x.id} value={x.id}>{x.name}</option>)}
            <option value="none">Organisation-wide only</option>
          </select>
        )}

        <div style={{ marginLeft: "auto", display: "flex", border: `1px solid ${theme.color.border}`, borderRadius: 3, overflow: "hidden" }}>
          {(["list", "matrix", "trend"] as const).map(v => (
            <button key={v} onClick={() => setView(v)} style={{
              padding: "6px 14px", fontSize: 12, fontWeight: view === v ? 600 : 400,
              background: view === v ? theme.color.accentBg : "transparent",
              color: view === v ? theme.color.accentText : theme.color.muted,
              border: "none", cursor: "pointer", textTransform: "capitalize",
            }}>
              {v === "list" ? "List" : v === "matrix" ? "Matrix" : "Trend"}
            </button>
          ))}
        </div>
      </div>

      {/* Arrived from the asset register: say what is being shown and how to leave it. */}
      {filterAsset && (
        <div style={{ ...card, padding: "8px 12px", marginBottom: 12, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", background: theme.color.accentBg, border: `1px solid ${theme.color.accent}` }}>
          <span style={{ fontSize: 12, color: theme.color.accentText }}>
            {risks.length > 0
              ? <>Showing the {risks.length} risk{risks.length === 1 ? "" : "s"} raised against <strong>{risks.find(r => r.asset)?.asset?.name ?? "this asset"}</strong>.</>
              : <>That asset has no risks raised against it yet.</>}
          </span>
          <button type="button" onClick={() => { const next = new URLSearchParams(searchParams); next.delete("asset"); setSearchParams(next, { replace: true }); }}
            style={{ padding: "5px 12px", fontSize: 12, borderRadius: 3, border: `1px solid ${theme.color.borderStrong}`, background: "transparent", color: theme.color.muted, cursor: "pointer" }}>
            Show every risk
          </button>
        </div>
      )}

      {/* Content */}
      {loading ? (
        <p style={{ color: theme.color.subtle, fontSize: 13 }}>Loading…</p>
      ) : visibleRisks.length === 0 && risks.length === 0 && !filterStatus && !filterFw && !filterSite && !filterAsset ? (
        <div style={{ ...emptyFrame, padding: 48 }}>
          <div style={{ marginBottom: 12, display: "flex", justifyContent: "center" }}><Shield size={32} color="var(--c-muted)" /></div>
          <div style={{ fontSize: 15, fontWeight: 600, color: theme.color.heading, marginBottom: 6 }}>No risks recorded yet</div>
          <div style={{ fontSize: 13, color: theme.color.subtle, marginBottom: canEdit ? 20 : 0 }}>
            {canEdit ? "Start by adding your first risk to begin building your register." : "Risks appear here once someone who manages the register adds them."}
          </div>
          {canEdit && <button onClick={() => setEditingRisk("new")} style={btnPrimary}>Add your first risk</button>}
        </div>
      ) : view === "matrix" ? (
        <div style={card}>
          <h3 style={{ margin: "0 0 20px", fontSize: 15, fontWeight: 600, color: theme.color.heading }}>5×5 Risk Matrix</h3>
          <RiskMatrix risks={visibleRisks} appetiteScore={appetite?.params.appetiteScore} />
        </div>
      ) : view === "trend" ? (
        <div style={{ ...card, padding: 20 }}>
          <h3 style={{ margin: "0 0 16px", fontSize: 15, fontWeight: 600, color: theme.color.heading }}>Risk Score Trend</h3>
          <TrendChart risks={risks} appetiteScore={appetite?.params.appetiteScore} />
        </div>
      ) : (
        <div style={{ ...card, padding: 0, overflow: "hidden" }}>
          {/* Table header */}
          <div style={{ display: "grid", gridTemplateColumns: "80px 2fr 1fr 100px 1fr 90px 90px 72px 110px", padding: "10px 16px", background: theme.color.panelAlt, borderBottom: "1px solid var(--c-line)", fontSize: 11, fontWeight: 600, color: theme.color.subtle, textTransform: "uppercase", letterSpacing: "0.04em" }}>
            <span>ID</span>
            <span>Risk</span>
            <span>Frameworks</span>
            <span>Priority</span>
            <span>Owner</span>
            <span style={{ textAlign: "center" }}>Inherent</span>
            <span style={{ textAlign: "center" }}>Residual</span>
            <span style={{ textAlign: "center" }} title="Heat = residual score + overdue treatments × 3">Heat</span>
            <span style={{ textAlign: "center" }}>Status</span>
          </div>

          {visibleRisks.length === 0 ? (
            <div style={{ padding: "24px 16px", textAlign: "center", color: theme.color.subtle, fontSize: 13 }}>
              {filterSite && filterSite !== "none" && !searchLower ? "No risk names this site yet — set the site on a risk to see it here." : "No risks match your search."}
            </div>
          ) : visibleRisks.map((r, i) => {
            const overdueReview = isOverdueReview(r);
            const overdueTreat  = hasOverdueTreatment(r);
            const treatSummary  = treatmentSummary(r);
            return (
              <div
                key={r.id}
                onClick={() => openRisk(r)}
                style={{
                  display: "grid", gridTemplateColumns: "80px 2fr 1fr 100px 1fr 90px 90px 72px 110px",
                  padding: "12px 16px", borderBottom: i < visibleRisks.length - 1 ? `1px solid ${theme.color.border}` : "none",
                  cursor: "pointer", transition: "background 0.1s",
                  alignItems: "center",
                }}
                onMouseEnter={e => (e.currentTarget.style.background = theme.color.panelAlt)}
                onMouseLeave={e => (e.currentTarget.style.background = "transparent")}
              >
                {/* ID */}
                <div style={{ fontSize: 11, fontVariantNumeric: "tabular-nums", color: theme.color.subtle }}>
                  {riskId(r) || "—"}
                </div>

                {/* Title + category + indicators */}
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: theme.color.heading, marginBottom: 2, display: "flex", alignItems: "center", gap: 6 }}>
                    {r.title}
                    {(overdueReview || overdueTreat) && (
                      <span title={overdueReview ? "Review overdue" : "Treatment overdue"} style={{ display: "inline-flex" }}>
                        <AlertTriangle size={12} color={theme.color.danger} />
                      </span>
                    )}
                    <AppetiteChip assessment={appetite?.assessments[r.id]} status={r.status} />
                    {r.suggestedForRemoval && (
                      <span
                        title={`This risk was tagged to framework(s) that have been removed: ${r.suggestedForRemovalFrameworks.join(", ")}. Review whether it should be retained or deleted.`}
                        style={{ fontSize: 10, padding: "1px 6px", borderRadius: 4, background: theme.color.warningBg, color: theme.color.warningText, fontWeight: 600, whiteSpace: "nowrap" }}
                      >
                        Review: framework removed
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: theme.color.subtle, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                    {r.category}
                    {r.linkedControl && (
                      <span style={{ fontSize: 10, fontVariantNumeric: "tabular-nums", color: theme.color.accentText, background: theme.color.accentBg, borderRadius: 3, padding: "0 4px" }}>
                        {r.linkedControl.id}
                      </span>
                    )}
                    {r.site && (
                      <span title={r.site.country} style={{ fontSize: 10, color: theme.color.accentText, background: theme.color.accentBg, borderRadius: 3, padding: "0 4px", display: "inline-flex", alignItems: "center", gap: 2 }}>
                        <MapPin size={9} />{r.site.name}
                      </span>
                    )}
                    {treatSummary && (
                      <span style={{ fontSize: 10, color: theme.color.subtle }}>· {treatSummary} treatments done</span>
                    )}
                    {r.reviewDate && (
                      <span style={{ fontSize: 10, color: overdueReview ? theme.color.dangerText : theme.color.subtle, display: "flex", alignItems: "center", gap: 2 }}>
                        <Clock size={9} />
                        {fmtDateOnly(r.reviewDate, { day: "numeric", month: "short" })}
                      </span>
                    )}
                  </div>
                  {/* The same tag row every register and assessment page carries:
                      type, department, office and jurisdiction. Clicking it must
                      not open the risk, so it stops the card's click. */}
                  <div onClick={(e) => e.stopPropagation()}>
                    <EntityTagsRow entity={{ kind: "risk", id: r.id }} style={{ marginTop: 6, marginBottom: 0 }} />
                  </div>
                </div>

                {/* Frameworks */}
                <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                  {r.frameworkIds.slice(0, 2).map(f => (
                    <span key={f} style={{ fontSize: 10, padding: "1px 6px", borderRadius: 4, background: theme.color.accentBg, color: theme.color.accentText }}>
                      {FRAMEWORK_LABELS[f as FrameworkId] ?? f}
                    </span>
                  ))}
                  {r.frameworkIds.length > 2 && <span style={{ fontSize: 10, color: theme.color.subtle }}>+{r.frameworkIds.length - 2}</span>}
                </div>

                {/* Priority */}
                <div>
                  {r.priority ? (
                    <span style={{
                      fontSize: 11, padding: "2px 8px", borderRadius: 4, fontWeight: 600,
                      background: priorityBg(r.priority), color: priorityColor(r.priority),
                    }}>
                      {r.priority.charAt(0).toUpperCase() + r.priority.slice(1)}
                    </span>
                  ) : (
                    <span style={{ fontSize: 11, color: theme.color.faint }}>—</span>
                  )}
                </div>

                {/* Owner */}
                <div style={{ fontSize: 12, color: theme.color.muted }}>{r.owner ?? "—"}</div>

                {/* Inherent */}
                <div style={{ textAlign: "center" }}>
                  <ScoreBadge score={r.inherentScore} size="sm" />
                </div>

                {/* Residual */}
                <div style={{ textAlign: "center" }}>
                  <ScoreBadge score={r.residualScore} size="sm" />
                  {r.targetScore != null && (
                    <div
                      title={r.residualScore != null && r.residualScore > r.targetScore ? "Residual score is above target" : "Target residual score"}
                      style={{ fontSize: 9, marginTop: 2, fontWeight: 600, color: r.residualScore != null && r.residualScore > r.targetScore ? theme.color.dangerText : theme.color.subtle }}
                    >
                      target {r.targetScore}
                    </div>
                  )}
                </div>

                {/* Heat score (client-side approximation) */}
                {(() => {
                  const base = r.residualScore ?? r.inherentScore ?? 0;
                  const overdueTreatCount = r.treatments.filter(t => t.status !== "completed" && t.dueDate && new Date(t.dueDate) < new Date()).length;
                  const heat = base + overdueTreatCount * 3 + (isOverdueReview(r) ? 5 : 0);
                  const heatColor = heat > 20 ? theme.color.dangerText : heat > 12 ? theme.color.warningText : heat > 0 ? theme.color.successText : theme.color.subtle;
                  return (
                    <div style={{ textAlign: "center" }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: heatColor }}>{heat > 0 ? heat : "—"}</span>
                    </div>
                  );
                })()}

                {/* Status */}
                <div style={{ textAlign: "center" }}>
                  <StatusPill status={r.status} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editingRisk !== null && (
        <RiskModal
          key={editingRisk === "new" ? "new" : editingRisk.id}
          risk={editingRisk === "new" ? null : editingRisk}
          canEdit={canEdit}
          onClose={closeRisk}
          onSaved={handleSaved}
          appetite={appetite}
        />
      )}

      {showAppetite && appetite && (
        <RiskAppetiteDrawer
          view={appetite}
          canEdit={canSetAppetite}
          onClose={() => setShowAppetite(false)}
          onSaved={() => setAppetiteVersion(v => v + 1)}
        />
      )}

      {showPanel && suggestions.length > 0 && (
        <SuggestionPanel
          suggestions={suggestions}
          onAccept={handleAcceptSuggestion}
          onClose={() => setShowPanel(false)}
          onAllReviewed={handleAllReviewed}
        />
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
